/**
 * 유지보수 예산 산정근거 — /maintenance 의 서브 탭 (v2.69.0).
 *
 * 화면 셋을 한 탭 안에서 오간다.
 *   ① 고객사 리스트   — 유지보수 계약을 한 번이라도 한 엔드유저 전부 + 직접 추가한 곳
 *   ② 납품내역 보기   — 고객사의 영업진행 납품 이력 (분류 배지 포함)
 *   ③ 산정근거 만들기 — 서버/스토리지/소프트웨어 중 선택 → 자동으로 채워진 표를
 *                      지우고 고쳐서 완성 → **요율(%) 입력 → 유지보수료 산출** → 저장
 *                      화면 위 [최종 산출] 에 세 분류 총액이 늘 보이고, 엑셀로 받을 수 있다.
 *
 * ⚠ 이름이 다른 곳(계약 '스마트서울' ↔ 영업 '서울시(스마트서울)')은 자동으로 잇지 않는다.
 *   고객사 행의 [이름 연결] 에서 별칭을 넣어야 납품내역이 합쳐진다.
 * ⚠ 분류 자동 판정은 넉넉히 걸린다 — 필요 없는 행은 지우는 것이 전제.
 * ⚠ 금액 경고 두 가지는 **지우지 않고 그대로 보여 준다** — 합계가 흔들릴 수 있다는 걸
 *   모르고 지나가는 게 제일 위험하다. (⚠단가 없음 / ⚠장비+디스크 한 행)
 */

import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as maintApi from '../lib/maintenance-api';
import { BUDGET_CATEGORY_LABEL, budgetFee } from '../lib/maintenance-api';
import type {
  BudgetCategory,
  BudgetCustomer,
  BudgetBasisItem,
} from '../lib/maintenance-api';
import { type Column, ColumnHeader, applyColState, useColState } from './DataTable';
import { useDialogLayer } from '../lib/dialog-stack';
import {
  rankStandard, recommendStandard, standardBadge, standardCategoryOf, standardDocName, standardIsSelf, standardLabelDiffers,
  standardLinkable, standardUnlinkableReason, type StandardRanked,
} from '../lib/budget-standard';
import { LoadingLines } from './ui/Skeleton';

// 영업번호·프로젝트명 클릭 → 영업건 요약 (보기 전용, v2.78.0 공용 — v2.79.1 에서 여기도)
const SalesSummaryModal = lazy(() =>
  import('./SalesSummaryModal').then((m) => ({ default: m.SalesSummaryModal })),
);

/** 금액 표기 — 빈 값은 '-' */
function won(n: number | null | undefined): string {
  return n === null || n === undefined ? '-' : n.toLocaleString('ko-KR');
}

const CATEGORIES: BudgetCategory[] = ['server', 'storage', 'software'];

const CAT_BADGE: Record<BudgetCategory, string> = {
  server: 'bg-sky-100 text-sky-700',
  storage: 'bg-violet-100 text-violet-700',
  software: 'bg-emerald-100 text-emerald-700',
};

type View =
  | { kind: 'list' }
  | { kind: 'deliveries'; customer: string }
  | { kind: 'basis'; customer: string; category: BudgetCategory | null }
  | { kind: 'prices' }
  | { kind: 'standards' };

export function MaintenanceBudgetBasisTab() {
  const [view, setView] = useState<View>({ kind: 'list' });

  if (view.kind === 'prices') {
    return <DeliveryPricesView onBack={() => setView({ kind: 'list' })} />;
  }
  if (view.kind === 'standards') {
    return <StandardMatchView onBack={() => setView({ kind: 'list' })} />;
  }
  if (view.kind === 'deliveries') {
    return (
      <DeliveriesView
        customer={view.customer}
        onBack={() => setView({ kind: 'list' })}
        onMakeBasis={(c) => setView({ kind: 'basis', customer: c, category: null })}
      />
    );
  }
  if (view.kind === 'basis') {
    return (
      <BasisView
        customer={view.customer}
        initialCategory={view.category}
        onBack={() => setView({ kind: 'list' })}
      />
    );
  }
  return (
    <CustomerListView
      onDeliveries={(c) => setView({ kind: 'deliveries', customer: c })}
      onBasis={(c, cat) => setView({ kind: 'basis', customer: c, category: cat })}
      onPrices={() => setView({ kind: 'prices' })}
      onStandards={() => setView({ kind: 'standards' })}
    />
  );
}

/* ═══ ① 고객사 리스트 ══════════════════════════════════ */

function CustomerListView({
  onDeliveries,
  onBasis,
  onPrices,
  onStandards,
}: {
  onDeliveries: (customer: string) => void;
  onBasis: (customer: string, category: BudgetCategory | null) => void;
  onPrices: () => void;
  /** 📄 표준 표기 매칭 (v2.316.1) */
  onStandards: () => void;
}) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [linking, setLinking] = useState<BudgetCustomer | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** 「제외한 곳 보기」 — 제외된 고객사까지 함께 보여 되돌릴 수 있게 */
  const [showHidden, setShowHidden] = useState(false);

  const query = useQuery({
    queryKey: ['maintenance', 'budgetCustomers', showHidden],
    queryFn: () => maintApi.listBudgetCustomers(showHidden),
    staleTime: 60_000,
  });

  const rows = useMemo(() => {
    const all = query.data?.customers ?? [];
    const kw = q.trim().toLowerCase();
    if (!kw) return all;
    return all.filter(
      (c) =>
        c.name.toLowerCase().includes(kw) ||
        c.aliases.some((a) => a.toLowerCase().includes(kw)),
    );
  }, [query.data, q]);

  const delMut = useMutation({
    mutationFn: (name: string) => maintApi.deleteBudgetCustomer(name),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['maintenance', 'budgetCustomers'] });
    },
    onError: (e) => setError((e as Error).message),
  });

  const xlsxMut = useMutation({
    mutationFn: () => maintApi.downloadBudgetExcel(),
    onSuccess: () => setError(null),
    onError: (e) => setError((e as Error).message),
  });

  const hideMut = useMutation({
    mutationFn: (v: { name: string; hidden: boolean }) =>
      maintApi.setBudgetCustomerHidden(v.name, v.hidden),
    onSuccess: (r) => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['maintenance', 'budgetCustomers'] });
      // 산정근거가 딸린 곳을 뺐으면 합계가 줄었다는 걸 말해 준다 (조용히 빠지면 안 된다)
      if (r.hidden && r.basisCount > 0) {
        setError(
          `'${r.name}' 을 목록에서 뺐습니다 — 작성된 산정근거 ${r.basisCount}건이 합계·총괄 엑셀에서 빠집니다. ` +
            '자료는 지워지지 않았고, [제외한 곳 보기] 에서 되돌릴 수 있습니다.',
        );
      }
    },
    onError: (e) => setError((e as Error).message),
  });

  // 산정근거를 한 건이라도 만든 고객사 · 요율까지 정해 유지보수료가 나온 합계
  // ⚠ 제외한 곳은 보고 있더라도 합계에서 뺀다 — 화면 합계와 총괄 엑셀이 달라지면 안 된다.
  const written = (query.data?.customers ?? []).filter((c) => !c.hidden && c.bases.length > 0);
  const priced = written.filter((c) => c.totalFee !== null);
  // ⚠ 아무도 요율을 안 정했으면 '0원' 이 아니라 '요율 미정' 이라고 말한다 —
  //   0원으로 보이면 유지보수료가 정말 0원인 줄 알고 지나간다.
  const grandFee = priced.length === 0 ? null : priced.reduce((s, c) => s + (c.totalFee ?? 0), 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="ui-section-title">유지보수 고객사</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            유지보수 계약을 한 번이라도 진행한 고객사 전부
            {query.data && <> — {query.data.customers.length}곳</>}. 새 계약이 생기면 자동으로
            추가됩니다.
            {written.length > 0 && (
              <>
                {' · 산정근거 '}
                <b className="text-slate-700">{written.length}곳</b>
                {' · 연간 유지보수료 합계 '}
                {grandFee === null ? (
                  <b className="text-amber-600">요율 미정</b>
                ) : (
                  <>
                    <b className="text-teal-700">{won(grandFee)}원</b>
                    {priced.length < written.length && (
                      <span className="text-amber-600">
                        {' '}
                        ({written.length - priced.length}곳 요율 미정)
                      </span>
                    )}
                  </>
                )}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="고객사 검색"
            className="ui-input w-40"
          />
          {(showHidden || (query.data?.hiddenCount ?? 0) > 0) && (
            <button
              type="button"
              onClick={() => setShowHidden((s) => !s)}
              title="예산 산정 대상이 아니라고 빼 둔 고객사 — 여기서 되돌릴 수 있습니다"
              className={`btn ${showHidden ? 'btn-dark' : 'btn-secondary'}`}
            >
              {showHidden ? '제외한 곳 숨기기' : `제외한 곳 보기 (${query.data?.hiddenCount ?? 0})`}
            </button>
          )}
          <button
            type="button"
            disabled={xlsxMut.isPending || written.length === 0}
            onClick={() => xlsxMut.mutate()}
            title="산정근거를 작성한 고객사 전부를 한 장에 — 분류별 도입금액·요율·유지보수료"
            className="btn btn-success"
          >
            {xlsxMut.isPending ? '만드는 중…' : '📊 예산 총괄 엑셀'}
          </button>
          <button
            type="button"
            onClick={onPrices}
            title="납품항목별 1개당 도입금액 — 산정근거의 금액·용량을 채우는 데 쓰는 표"
            className="btn btn-warning"
          >
            💰 단가표 작성
          </button>
          <button
            type="button"
            onClick={onStandards}
            title="SM 내부 명칭(IFT 16TB 등)마다 표준 표기(나라장터 쇼핑몰 등록 명칭)를 잇습니다 — 산정근거 최종 문서 품목 칸에 이 이름이 적힙니다"
            className="btn btn-outline"
          >
            📄 표준 표기 매칭
          </button>
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="btn btn-success"
          >
            + 새 고객사 추가
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm bg-rose-50 border border-rose-200 text-rose-700 rounded px-3 py-2">
          {error}
        </div>
      )}

      {query.isLoading && (
        <div className="bg-white rounded border border-slate-200 p-12 text-center text-slate-400"><LoadingLines /></div>
      )}
      {query.isError && (
        <div className="bg-white rounded border border-rose-200 p-6 text-center text-rose-600">
          오류: {(query.error as Error).message}
        </div>
      )}

      {query.data && (
        <div className="bg-white rounded border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-xs">
                  {/* ⚠ 폭 실측(1920·1536·1366·1280) 후 정한 값 — 「연간 유지보수료」 칸과 [제외] 버튼을
                      더하면서 1280 에서 작업 버튼이 2줄로 갈라졌다. 버튼 글자를 줄이고,
                      계약 건수·기간을 한 칸에 합쳐 자리를 만들어 1줄로 맞췄다.
                      여기 숫자를 늘리려면 1280 을 다시 재 볼 것. */}
                  <th className="text-left font-medium px-3 py-2 min-w-[150px]">고객사</th>
                  <th className="text-center font-medium px-2 py-2 w-[112px]">계약</th>
                  <th className="text-right font-medium px-2 py-2 w-[68px]" title="이 고객사의 영업건 전부">
                    영업건
                  </th>
                  {/* v2.79.6 — 기본 정렬 기준. 헤더가 좁아 꺾이지 않게 whitespace-nowrap */}
                  <th
                    className="text-right font-medium px-2 py-2 w-[92px] whitespace-nowrap"
                    title="납품내역에서 실제로 살아남는 영업건 (계산서 미발행 · ⊘ 제외 · 용역 건을 뺀 수) — 이 순서로 정렬됩니다"
                  >
                    유지보수 대상
                  </th>
                  <th className="text-left font-medium px-2 py-2 w-[138px]">작성된 산정근거</th>
                  <th className="text-right font-medium px-2 py-2 w-[118px]">연간 유지보수료</th>
                  <th className="text-right font-medium px-3 py-2 w-[248px]">작업</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-10 text-center text-slate-400">
                      해당하는 고객사가 없습니다.
                    </td>
                  </tr>
                )}
                {rows.map((c) => (
                  <tr
                    key={c.name}
                    className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 ${
                      c.hidden ? 'bg-slate-50/70 text-slate-400' : ''
                    }`}
                  >
                    <td className="px-3 py-2">
                      <div className={`font-medium ${c.hidden ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                        {c.name}
                        {c.manual && (
                          <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                            직접 추가
                          </span>
                        )}
                        {c.hidden && (
                          <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 no-underline">
                            제외됨
                          </span>
                        )}
                      </div>
                      {c.aliases.length > 0 && (
                        <div className="text-xs text-slate-500 mt-0.5">
                          영업진행 표기: {c.aliases.join(' · ')}
                        </div>
                      )}
                    </td>
                    {/* 계약 건수 + 기간을 한 칸에 (폭 확보) */}
                    <td className="px-2 py-2 text-center tabular-nums text-slate-600">
                      {c.contractCount > 0 ? (
                        <>
                          <div>{c.contractCount}건</div>
                          {c.firstYear && (
                            <div className="text-xs text-slate-500">
                              {c.firstYear}~{c.lastYear}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums ${
                        c.salesCount === 0 ? 'text-rose-500' : 'text-slate-600'
                      }`}
                    >
                      {c.salesCount}건
                    </td>
                    {/*
                      유지보수 대상 영업건 (v2.79.6) — 목록의 기본 정렬 기준.
                      뺀 내역을 툴팁으로 밝힌다: 숫자만 주면 왜 줄었는지 알 수 없다.
                    */}
                    <td className="px-2 py-2 text-right tabular-nums">
                      <span
                        className={
                          c.validSalesCount === 0 ? 'text-slate-300' : 'font-medium text-slate-800'
                        }
                        title={
                          c.salesCount === c.validSalesCount
                            ? '전부 유지보수 대상입니다'
                            : `전체 ${c.salesCount}건에서 뺀 것 — ${[
                                c.salesExcluded.unissued > 0 ? `계산서 미발행 ${c.salesExcluded.unissued}` : '',
                                c.salesExcluded.excluded > 0 ? `⊘ 제외 ${c.salesExcluded.excluded}` : '',
                                c.salesExcluded.service > 0 ? `용역 ${c.salesExcluded.service}` : '',
                              ].filter((s) => s !== '').join(' · ')}건`
                        }
                      >
                        {c.validSalesCount}건
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {c.bases.length === 0 ? (
                        <span className="text-slate-400 text-xs">-</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {c.bases.map((b) => (
                            <span
                              key={b.category}
                              className={`text-xs px-1.5 py-0.5 rounded ${CAT_BADGE[b.category]}`}
                            >
                              {BUDGET_CATEGORY_LABEL[b.category]} {b.items}행
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    {/* 요율을 아직 안 정했으면 금액이 아니라 '요율 미정' 이라고 말한다 —
                        0 원으로 보이면 유지보수료가 0 인 줄 안다 */}
                    <td className="px-3 py-2 text-right tabular-nums">
                      {c.bases.length === 0 ? (
                        <span className="text-slate-400 text-xs">-</span>
                      ) : c.totalFee === null ? (
                        <span className="text-amber-600 text-xs">요율 미정</span>
                      ) : (
                        <>
                          <span className="font-medium text-teal-700">{won(c.totalFee)}</span>
                          {c.bases.some((b) => b.fee === null) && (
                            <div className="text-xs text-amber-600">일부 요율 미정</div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1.5">
                        {c.hidden ? (
                          <button
                            type="button"
                            disabled={hideMut.isPending}
                            onClick={() => hideMut.mutate({ name: c.name, hidden: false })}
                            title="다시 예산 산정 대상으로 되돌립니다"
                            className="btn btn-success btn-sm"
                          >
                            ↩ 되돌리기
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => onDeliveries(c.name)}
                              className="btn btn-secondary btn-sm"
                            >
                              📦 납품내역
                            </button>
                            <button
                              type="button"
                              onClick={() => onBasis(c.name, null)}
                              title="산정근거 만들기 · 요율을 넣으면 유지보수료가 나옵니다"
                              className="btn btn-outline btn-sm"
                            >
                              📐 산정근거
                            </button>
                            {/*
                              🔗 에 **연결할 게 있는지**를 겉에 드러낸다 (사용자 지적 08-20).
                              후보가 없는 곳이 대부분이라, 없는 것을 확인하려고 하나하나 눌러
                              보게 두면 안 된다. 0 이면 흐리게 — 막지는 않는다(이미 연결한
                              이름을 풀러 들어갈 수 있어야 하므로).
                            */}
                            <button
                              type="button"
                              onClick={() => setLinking(c)}
                              title={
                                c.aliasCandidates > 0
                                  ? `연결할 수 있는 이름 ${c.aliasCandidates}개가 있습니다 — 눌러서 확인하세요`
                                  : '이름이 비슷한 영업진행 고객사가 없습니다'
                              }
                              className={`btn btn-sm ${c.aliasCandidates > 0 ? 'btn-warning' : 'btn-secondary'}`}
                            >
                              🔗
                              {c.aliasCandidates > 0 && (
                                <span className="ml-1 tabular-nums">{c.aliasCandidates}</span>
                              )}
                            </button>
                            {/* 제외 / 삭제 — 계약에서 자동으로 나오는 곳은 지울 수 없으니 '제외'로 뺀다.
                                직접 추가한 곳은 아예 지운다(되돌릴 게 없으므로). */}
                            <button
                              type="button"
                              disabled={hideMut.isPending || delMut.isPending}
                              onClick={() => {
                                const hasBasis = c.bases.length > 0;
                                if (c.manual) {
                                  if (window.confirm(`'${c.name}' 을 목록에서 아주 지울까요? (직접 추가한 고객사)`)) {
                                    delMut.mutate(c.name);
                                  }
                                  return;
                                }
                                const msg =
                                  `'${c.name}' 을 예산 산정 대상에서 빼시겠습니까?\n\n` +
                                  '· 계약에서 자동으로 나오는 고객사라 지워지지는 않고 목록에서만 빠집니다.\n' +
                                  '· [제외한 곳 보기] 에서 언제든 되돌릴 수 있습니다.' +
                                  (hasBasis ? `\n\n⚠ 작성된 산정근거 ${c.bases.length}건이 합계에서 빠집니다 (자료는 남습니다).` : '');
                                if (window.confirm(msg)) hideMut.mutate({ name: c.name, hidden: true });
                              }}
                              title={c.manual ? '이 고객사를 아주 지웁니다' : '예산 산정 대상에서 뺍니다 (되돌릴 수 있음)'}
                              className="btn btn-danger btn-sm"
                            >
                              ✕
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {adding && <AddCustomerModal onClose={() => setAdding(false)} />}
      {linking && <LinkNamesModal customer={linking} onClose={() => setLinking(null)} />}
    </div>
  );
}

/* ── 새 고객사 추가 ─────────────────────────────────── */

function AddCustomerModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => maintApi.createBudgetCustomer({ name: name.trim(), note: note.trim() || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['maintenance', 'budgetCustomers'] });
      onClose();
    },
    onError: (e) => setError((e as Error).message),
  });

  return (
    <ModalShell title="새 고객사 추가" onClose={onClose}>
      <p className="text-xs text-slate-500 mb-3">
        유지보수 계약이 아직 없는 곳을 미리 넣어 둘 때 씁니다. 계약이 있는 고객사는 목록에 이미
        자동으로 나와 있습니다.
      </p>
      <label className="ui-label block mb-3">
        <span className="text-slate-600">고객사 이름</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          className="ui-input mt-1 w-full"
          placeholder="예) 강서구청"
        />
      </label>
      <label className="ui-label block mb-4">
        <span className="text-slate-600">메모 (선택)</span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          className="ui-input ui-textarea mt-1 w-full"
        />
      </label>
      {error && (
        <div className="text-sm bg-rose-50 border border-rose-200 text-rose-700 rounded px-3 py-2 mb-3">
          {error}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="btn btn-secondary">
          취소
        </button>
        <button
          type="button"
          disabled={name.trim() === '' || mut.isPending}
          onClick={() => mut.mutate()}
          className="btn btn-success-solid"
        >
          {mut.isPending ? '추가 중…' : '추가'}
        </button>
      </div>
    </ModalShell>
  );
}

/* ── 이름 연결(별칭) ────────────────────────────────── */

function LinkNamesModal({ customer, onClose }: { customer: BudgetCustomer; onClose: () => void }) {
  const qc = useQueryClient();
  const [aliases, setAliases] = useState<string[]>(customer.aliases);
  const [error, setError] = useState<string | null>(null);

  const cand = useQuery({
    queryKey: ['maintenance', 'aliasCandidates', customer.name],
    queryFn: () => maintApi.listAliasCandidates(customer.name),
    staleTime: 60_000,
  });

  const mut = useMutation({
    mutationFn: () => maintApi.updateBudgetCustomer(customer.name, { aliases, note: customer.note }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['maintenance', 'budgetCustomers'] });
      onClose();
    },
    onError: (e) => setError((e as Error).message),
  });

  function toggle(n: string) {
    setAliases((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));
  }

  return (
    <ModalShell title={`이름 연결 — ${customer.name}`} onClose={onClose}>
      <p className="text-xs text-slate-500 mb-3">
        계약의 고객사 이름과 영업진행의 엔드유저 이름이 다를 때 씁니다. 여기서 연결한 이름의
        영업건이 이 고객사의 납품내역에 함께 잡힙니다. <b>같은 곳이 확실할 때만</b> 연결하세요.
      </p>

      {cand.isLoading && <div className="text-sm text-slate-400 py-3">후보 찾는 중…</div>}
      {cand.data && cand.data.candidates.length === 0 && (
        <div className="text-sm text-slate-400 py-3">이름이 비슷한 영업진행 고객사가 없습니다.</div>
      )}
      {cand.data && cand.data.candidates.length > 0 && (
        <div className="space-y-1 mb-3 max-h-60 overflow-y-auto">
          {cand.data.candidates.map((c) => (
            <label
              key={c.name}
              className="flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
            >
              <input type="checkbox" checked={aliases.includes(c.name)} onChange={() => toggle(c.name)} />
              <span className="text-slate-700">{c.name}</span>
              <span className="text-xs text-slate-500">영업건 {c.salesCount}건</span>
            </label>
          ))}
        </div>
      )}

      {aliases.filter((a) => !(cand.data?.candidates ?? []).some((c) => c.name === a)).length > 0 && (
        <div className="text-xs text-slate-500 mb-3">
          이미 연결됨: {aliases.join(' · ')}
        </div>
      )}

      {error && (
        <div className="text-sm bg-rose-50 border border-rose-200 text-rose-700 rounded px-3 py-2 mb-3">
          {error}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="btn btn-secondary">
          취소
        </button>
        <button
          type="button"
          disabled={mut.isPending}
          onClick={() => mut.mutate()}
          className="btn btn-primary"
        >
          {mut.isPending ? '저장 중…' : '저장'}
        </button>
      </div>
    </ModalShell>
  );
}

/* ═══ ② 납품내역 보기 ══════════════════════════════════ */

function DeliveriesView({
  customer,
  onBack,
  onMakeBasis,
}: {
  customer: string;
  onBack: () => void;
  onMakeBasis: (customer: string) => void;
}) {
  const [cat, setCat] = useState<BudgetCategory | 'all'>('all');
  /** [매입 N건] 클릭 → 연결된 매입내역 팝업 */
  const [purchasePopup, setPurchasePopup] = useState<maintApi.BudgetDeliveryRow | null>(null);
  /** ✎/⊘ 클릭 → 수동 수정/제외 모달 (v2.77.3) */
  const [overrideModal, setOverrideModal] = useState<{
    row: maintApi.BudgetDeliveryRow;
    mode: 'edit' | 'exclude';
  } | null>(null);
  /** 영업번호·프로젝트명 클릭 → 영업건 요약 (v2.79.1) */
  const [salesSummaryId, setSalesSummaryId] = useState<string | null>(null);
  /**
   * ⊘ 제외 행 숨기기 — **기본이 숨김**(v2.79.4 사용자 08-20). 제외한 행은 이미 판단이
   * 끝난 행이라 늘 보일 이유가 없다. 숨겨도 머리말 건수와 토글 라벨이 몇 건인지 말해 주고,
   * [보이기] 한 번으로 회색 취소선 행이 되돌아온다 (자료는 그대로).
   */
  const [hideExcluded, setHideExcluded] = useState(true);
  /** 칼럼 정렬·필터 (v2.79.1) — 영업진행·유지보수 표와 같은 패턴 */
  const colState = useColState();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['maintenance', 'budgetDeliveries', customer],
    queryFn: () => maintApi.listBudgetDeliveries(customer),
    staleTime: 60_000,
  });

  /**
   * 수동 지시가 손대는 화면 **네 군데 전부** 새로고침.
   * ⚠ 산정근거(자동채움)·최종 산출을 빼먹으면, 제외해 놓고 산정근거를 열었을 때
   *   뺀 행이 그대로 남은 옛 표가 뜬다 — 반영처가 넷인데 갱신이 둘이면 갈라진다.
   */
  const refreshAfterOverride = () => {
    qc.invalidateQueries({ queryKey: ['maintenance', 'budgetDeliveries', customer] });
    qc.invalidateQueries({ queryKey: ['maintenance', 'deliveryPrices'] });
    qc.invalidateQueries({ queryKey: ['maintenance', 'budgetBasis'] });
    qc.invalidateQueries({ queryKey: ['maintenance', 'budgetSummary'] });
  };

  const revertMut = useMutation({
    mutationFn: (row: maintApi.BudgetDeliveryRow) =>
      maintApi.clearDeliveryOverride({ salesId: row.salesId, legacyId: row.legacyId }),
    onSuccess: refreshAfterOverride,
  });

  const catRows = (query.data?.rows ?? []).filter(
    (r) => cat === 'all' || r.categories.includes(cat),
  );

  const manualEdited = (query.data?.rows ?? []).filter((r) => r.override?.action === 'edit').length;
  const manualExcluded = (query.data?.rows ?? []).filter((r) => r.override?.action === 'exclude').length;
  /** 지금 분류 필터 안에서 숨길 수 있는 제외 행 수 — 버튼 라벨용 */
  const excludedInView = catRows.filter((r) => r.override?.action === 'exclude').length;

  /**
   * 칼럼 정의 (v2.79.1) — 헤더 클릭 정렬 + 호버 🔍 필터.
   * ⭐분류(추정)를 맨 왼쪽으로 (사용자 08-20). accessor 는 필터 목록에 사람이 읽을 값이
   *   나오게 — 분류는 한글 라벨, 날짜는 원문(YYYY-MM-DD).
   * 매입·관리 칸은 정렬할 값이 아니라 여기 안 넣고 평범한 th 로 남긴다.
   */
  const DELIVERY_COLUMNS: Column<maintApi.BudgetDeliveryRow>[] = useMemo(
    () => [
      {
        key: 'categories',
        label: '분류(추정)',
        type: 'text',
        nowrapHeader: true,
        accessor: (r) =>
          r.service
            ? '용역(제외)'
            : r.categories.length === 0
              ? '(없음)'
              : r.categories.map((c) => BUDGET_CATEGORY_LABEL[c]).join(' · '),
      },
      { key: 'introDate', label: '도입날짜', type: 'date',
        nowrapHeader: true, accessor: (r) => r.introDate ?? '' },
      {
        key: 'salesId',
        label: '영업번호',
        type: 'text',
        nowrapHeader: true,
        accessor: (r) => r.salesId ?? '과거자료',
      },
      { key: 'projectName', label: '프로젝트명', type: 'text', accessor: (r) => r.projectName ?? '' },
      { key: 'item', label: '제안품목', type: 'text', accessor: (r) => r.item ?? '' },
      {
        key: 'derived',
        label: '납품내역(산정)',
        type: 'text',
        accessor: (r) => (r.override?.action === 'exclude' ? `제외: ${r.derived}` : r.derived),
      },
      { key: 'usage', label: '용도', type: 'text',
        nowrapHeader: true, accessor: (r) => r.usage ?? '' },
    ],
    [],
  );

  const rows = useMemo(() => {
    const base = hideExcluded
      ? catRows.filter((r) => r.override?.action !== 'exclude')
      : catRows;
    // 정렬을 안 건드리면 서버 순서(도입날짜 내림차순) 그대로다
    return applyColState(base, DELIVERY_COLUMNS, colState);
  }, [catRows, hideExcluded, DELIVERY_COLUMNS, colState]);

  return (
    <div className="space-y-3">
      <ViewHeader
        onBack={onBack}
        title={`납품내역 — ${customer}`}
        subtitle={
          query.data
            ? `영업진행 ${query.data.rows.length - (query.data.legacyCount ?? 0)}건${
                (query.data.legacyCount ?? 0) > 0 ? ` · 과거자료 ${query.data.legacyCount}건` : ''
              }${
                /* 조용히 빠지면 목록이 짧아진 걸 아무도 모른다 */
                (query.data.unissuedCount ?? 0) > 0
                  ? ` · 계산서 미발행 ${query.data.unissuedCount}건 제외`
                  : ''
              }${
                /* 수동 지시도 머리말에 — 몇 건을 사람이 고쳤는지 숨기지 않는다 */
                manualEdited > 0 ? ` · 수동 수정 ${manualEdited}건` : ''
              }${manualExcluded > 0 ? ` · 수동 제외 ${manualExcluded}건` : ''}${
                query.data.matchedNames.length > 1
                  ? ` · 연결된 이름: ${query.data.matchedNames.join(' · ')}`
                  : ''
              }`
            : ''
        }
        right={
          <button
            type="button"
            onClick={() => onMakeBasis(customer)}
            className="btn btn-outline"
          >
            📐 산정근거 만들기
          </button>
        }
      />

      <div className="flex items-center gap-1.5 flex-wrap">
        <FilterChip active={cat === 'all'} onClick={() => setCat('all')} label="전체" />
        {CATEGORIES.map((c) => (
          <FilterChip
            key={c}
            active={cat === c}
            onClick={() => setCat(c)}
            label={BUDGET_CATEGORY_LABEL[c]}
          />
        ))}
        <span className="text-xs text-slate-500 ml-1">{rows.length}건</span>
        {/* ⊘ 제외 행 숨김 토글 (v2.79.1) — 몇 건을 숨기는지 라벨이 말해 준다 */}
        {excludedInView > 0 && (
          <button
            type="button"
            onClick={() => setHideExcluded((v) => !v)}
            title={hideExcluded ? '숨긴 제외 행을 다시 보입니다' : '⊘ 제외 처리한 행을 목록에서 감춥니다 (자료는 그대로)'}
            className={`btn btn-sm ml-2 ${hideExcluded ? 'btn-danger' : 'btn-secondary'}`}
          >
            {hideExcluded ? `⊘ 제외 ${excludedInView}건 숨김 중 — 보이기` : `⊘ 제외 ${excludedInView}건 숨기기`}
          </button>
        )}
        {/* 칼럼 필터·정렬이 걸려 있으면 초기화 버튼 */}
        {(colState.sortStack.length > 0 || Object.keys(colState.filters).length > 0) && (
          <button
            type="button"
            onClick={() => colState.resetAll()}
            className="btn btn-outline btn-sm"
          >
            ↺ 필터·정렬 초기화
          </button>
        )}
      </div>

      {query.isLoading && (
        <div className="bg-white rounded border border-slate-200 p-12 text-center text-slate-400"><LoadingLines /></div>
      )}
      {query.isError && (
        <div className="bg-white rounded border border-rose-200 p-6 text-center text-rose-600">
          오류: {(query.error as Error).message}
        </div>
      )}

      {/*
        납품항목별 합계 수량 — 이 고객사가 무엇을 몇 개 갖고 있는지 (사용자 요청 08-12).
        ⚠ 분류 필터(cat)와 무관하게 **늘 전체 합계**다. 필터에 따라 합계가 흔들리면
          "이 고객사에 총 몇 개"라는 질문의 답이 될 수 없다 — 그래서 그렇게 적어 둔다.
        원문 그대로인 항목은 흐리게 — SM 내부 명칭이 아니라 합계의 신뢰도가 다르다.
      */}
      {query.data && query.data.totals.length > 0 && (
        <div className="bg-white rounded border border-slate-200 overflow-hidden">
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs text-slate-600">
            납품항목별 합계 수량 — <b className="text-slate-900">{query.data.totals.length}종</b> ·
            총{' '}
            <b className="text-slate-900">
              {query.data.totals.reduce((s, t) => s + t.qty, 0).toLocaleString('ko-KR')}
            </b>
            개
            <span className="text-slate-400"> (분류 필터와 무관한 전체 합계입니다)</span>
            {/*
              ⊘ 제외분은 서버가 이미 빼고 센다 — 그런데 화면이 그 말을 안 해서
              「숨기기를 눌러도 위 숫자가 안 변한다 = 아직 들어 있나 보다」로 읽혔다.
              몇 건을 빼고 센 숫자인지 여기서 밝힌다 (사용자 08-20).
            */}
            {manualExcluded > 0 && (
              <span className="text-rose-600"> · ⊘ 제외 {manualExcluded}건은 빼고 센 숫자입니다</span>
            )}
          </div>
          <div className="p-2 flex flex-wrap gap-1.5">
            {query.data.totals.map((t) => (
              <span
                key={t.label}
                title={`영업건 ${t.rows}건에서 납품${t.standard ? '' : ' · 규칙이 없어 원문 그대로인 표기입니다'}`}
                className={`inline-flex items-baseline gap-1.5 px-2 py-1 rounded border text-xs ${
                  t.standard
                    ? 'bg-white border-slate-300 text-slate-700'
                    : 'bg-slate-50 border-slate-200 text-slate-400'
                }`}
              >
                <span>{t.label}</span>
                <b className="text-slate-900 tabular-nums">{t.qty.toLocaleString('ko-KR')}</b>
                <span className="text-slate-400">개</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {query.data && (
        <div className="bg-white rounded border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-xs">
                  {/* ⚠ 폭 실측(1920·1536·1366·1280) 후 정한 값 — 「납품내역(산정)」·「매입」을 더하며
                      날짜·번호를 좁혔다. 여기 숫자를 늘리려면 1280 을 다시 재 볼 것.
                      v2.79.1: 분류(추정)를 맨 왼쪽으로 + 헤더 클릭 정렬·호버 🔍 필터 (ColumnHeader).
                      매입·관리는 정렬할 값이 아니라 평범한 th 로 남긴다. */}
                  {DELIVERY_COLUMNS.map((c) => (
                    <ColumnHeader
                      key={c.key}
                      column={c}
                      baseRows={catRows}
                      columns={DELIVERY_COLUMNS}
                      colState={colState}
                    />
                  ))}
                  <th className="text-center font-medium px-2 py-2 w-[76px]">매입</th>
                  <th className="text-center font-medium px-2 py-2 w-[64px]">관리</th>
                </tr>
              </thead>
              {rows.length === 0 && (
                <tbody>
                  <tr>
                    <td colSpan={9} className="px-3 py-10 text-center text-slate-400">
                      해당하는 납품내역이 없습니다.
                    </td>
                  </tr>
                </tbody>
              )}
              {rows.map((r) => {
                /**
                 * 한 건에 항목이 여럿이면 **항목마다 표의 행으로 분리** (v2.79.3 · 사용자 08-20).
                 * 건 공통 칸(분류·날짜·번호·프로젝트·제안품목·용도·매입·관리)은 rowSpan 으로
                 * 한 번만 적는다 — 항목 행마다 반복해 적으면 별개 납품처럼 읽힌다.
                 * 산정 칸만 항목 수만큼 나뉘며, 수정/제외 배지는 첫 행·사유/산정방법 메모는
                 * 끝 행에 붙는다 (엑셀 병합셀 모양). 한 건 = 한 <tbody> — 어느 항목 행에
                 * 마우스를 올려도 건 전체가 함께 하이라이트된다.
                 */
                const split = r.lines.length > 1 ? r.lines : null;
                const span = split ? split.length : 1;
                const shared = split ? ' align-top' : '';
                return (
                  <tbody
                    key={r.salesId ?? `L${r.legacyId}`}
                    className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 ${
                      /* 수동 제외 행 — 지우지 않고 회색으로 남긴다 (조용히 사라지면 아무도 모른다) */
                      r.override?.action === 'exclude' ? 'opacity-60' : ''
                    }`}
                  >
                  <tr>
                    {/*
                      분류(추정) — v2.79.1 맨 왼쪽으로 · v2.79.5 **항목마다 하나**.
                      ⚠ 여기만 rowSpan 으로 묶지 않는다 — 항목마다 분류가 다를 수 있어서다
                        (서버 행과 소프트웨어 행이 한 건에 같이 있을 수 있다).
                    */}
                    <td className={`px-2 py-1.5 align-top${split ? ' border-b border-slate-100' : ''}`}>
                      <CategoryCell row={r} line={split ? split[0]! : (r.lines[0] ?? null)} />
                    </td>
                    <td rowSpan={span} className={`px-2 py-1.5 whitespace-nowrap tabular-nums text-slate-700 text-xs${shared}`}>
                      {/* 과거자료엔 년월만 아는 행이 있다 — 1일로 채운 날짜를 그대로 쓰면 없는 정밀도가 생긴다 */}
                      {r.introDate === null ? (
                        <span className="text-slate-300">{r.salesId === null ? '-' : '미발행'}</span>
                      ) : r.datePrecision === 'month' ? (
                        r.introDate.slice(0, 7)
                      ) : (
                        r.introDate
                      )}
                    </td>
                    <td rowSpan={span} className={`px-2 py-1.5 text-xs tabular-nums${shared}`}>
                      {r.salesId !== null ? (
                        // 클릭 → 영업건 요약 (보기 전용, v2.79.1)
                        <button
                          type="button"
                          onClick={() => setSalesSummaryId(r.salesId!)}
                          title="클릭 → 이 영업건 정보 보기"
                          className="text-slate-500 underline decoration-dotted underline-offset-2 hover:decoration-solid hover:text-blue-700"
                        >
                          {r.salesId}
                        </button>
                      ) : (
                        <span
                          className="inline-block px-1.5 py-0.5 rounded bg-amber-50 border border-amber-300 text-amber-700 text-xs whitespace-nowrap"
                          title="영업진행에 없어 기초자료 엑셀에서 가져온 과거 납품입니다"
                        >
                          과거자료
                        </span>
                      )}
                    </td>
                    <td rowSpan={span} className={`px-2 py-1.5 text-slate-600 text-xs${shared}`}>
                      {r.projectName === null ? (
                        '-'
                      ) : r.salesId !== null ? (
                        // 프로젝트명 클릭도 같은 팝업 (v2.79.1). 과거자료 행은 영업건이 없어 글씨만.
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={() => setSalesSummaryId(r.salesId!)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSalesSummaryId(r.salesId!);
                            }
                          }}
                          title="클릭 → 이 영업건 정보 보기"
                          className="cursor-pointer underline decoration-dotted underline-offset-2 hover:decoration-solid hover:text-blue-700"
                        >
                          {r.projectName}
                        </span>
                      ) : (
                        r.projectName
                      )}
                    </td>
                    <td rowSpan={span} className={`px-2 py-1.5 text-slate-500 text-xs${shared}`}>{r.item ?? '-'}</td>
                    {/*
                      납품내역(산정) — v2.79.2 항목·수량 분리 표기 (사용자 08-20).
                      항목마다 한 줄: 왼쪽 이름 · 오른쪽 수량. 같은 격자 줄이라 이름이 두 줄로
                      감겨도 수량이 남의 항목 것으로 밀리지 않는다 (칸을 둘로 나누면 어긋난다 — 실측).
                    */}
                    <td className={`px-2 py-1.5 align-top${split ? ' border-b border-slate-100' : ''}`}>
                      {split ? (
                        // 분리 첫 행 — 첫 항목 + (수정/제외면) 배지. 사유·방법 메모는 끝 행에.
                        <SplitDerivedItem r={r} line={split[0]!} isFirst isLast={false} />
                      ) : r.override?.action === 'exclude' ? (
                        <>
                          <div className="text-xs mb-0.5">
                            <span className="inline-block px-1.5 py-0.5 rounded bg-rose-50 border border-rose-300 text-rose-700 text-xs">
                              제외
                            </span>
                          </div>
                          <DerivedLines
                            lines={r.lines}
                            fallback={r.derived === '' ? '-' : r.derived}
                            nameCls="text-slate-400 line-through"
                            qtyCls="text-slate-400 line-through"
                          />
                          {/* 사유는 제외에선 선택 (v2.79.6) — 비면 「사유: 」 빈 줄을 만들지 않는다 */}
                          {r.override.reason !== '' && (
                            <div className="text-xs text-rose-600 mt-0.5">사유: {r.override.reason}</div>
                          )}
                        </>
                      ) : r.override?.action === 'edit' ? (
                        <>
                          <div className="text-xs mb-0.5">
                            <span className="inline-block px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-300 text-indigo-700 text-xs">
                              수정
                            </span>
                          </div>
                          <DerivedLines lines={r.lines} fallback={r.derived} />
                          <div className="text-xs text-indigo-600 mt-0.5">사유: {r.override.reason}</div>
                          {/* 무엇을 고쳤는지 화면에서 보이게 — 자동 산정값을 옆에 남긴다 */}
                          <div className="text-xs text-slate-500 mt-0.5">
                            자동: {r.override.autoDerived === '' ? '(없음)' : r.override.autoDerived}
                          </div>
                        </>
                      ) : r.derived === '' ? (
                        <span className="text-slate-400 text-xs">
                          {r.method === '용역(제외)' ? '용역(제외)' : '-'}
                        </span>
                      ) : (
                        <>
                          <DerivedLines lines={r.lines} fallback={r.derived} />
                          <div className="text-xs text-slate-500 mt-0.5">
                            {r.method}
                            {r.derivedNote !== '' && <> · {r.derivedNote}</>}
                          </div>
                        </>
                      )}
                    </td>
                    <td rowSpan={span} className={`px-2 py-1.5${shared}`}>
                      {/*
                        ⚠ key 에 서버 값을 넣어 **목록을 다시 받으면 칸도 다시 맞춘다**.
                          안 그러면 남이 채운 용도를 내 화면은 빈칸으로 들고 있다가,
                          그 칸을 스쳐 지나가기만 해도 빈 값으로 덮어 지운다.
                      */}
                      <UsageCell key={`${r.salesId ?? `L${r.legacyId}`}|${r.usage ?? ''}`} row={r} />
                    </td>
                    <td rowSpan={span} className={`px-2 py-1.5 text-center${shared}`}>
                      {r.purchases.length === 0 ? (
                        <span className="text-slate-400 text-xs">-</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setPurchasePopup(r)}
                          title="연결된 매입내역 보기"
                          className="btn btn-secondary btn-sm"
                        >
                          {r.purchases.length}건
                        </button>
                      )}
                    </td>
                    <td rowSpan={span} className={`px-2 py-1.5 text-center whitespace-nowrap${shared}`}>
                      {r.override === null ? (
                        <>
                          <button
                            type="button"
                            onClick={() => setOverrideModal({ row: r, mode: 'edit' })}
                            title="자동 산정이 틀렸을 때 이 행의 납품내역을 손으로 고칩니다"
                            className="btn btn-secondary btn-sm"
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            onClick={() => setOverrideModal({ row: r, mode: 'exclude' })}
                            title="이 행을 합계·산정근거·단가표에서 뺍니다 (화면에는 회색으로 남습니다)"
                            className="btn btn-danger btn-sm ml-1"
                          >
                            ⊘
                          </button>
                        </>
                      ) : (
                        <>
                          {r.override.action === 'edit' && (
                            <button
                              type="button"
                              onClick={() => setOverrideModal({ row: r, mode: 'edit' })}
                              title="수동 수정 내용 고치기"
                              className="btn btn-outline btn-sm"
                            >
                              ✎
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm('수동 지시를 되돌릴까요? 자동 산정 결과로 돌아갑니다.')) {
                                revertMut.mutate(r);
                              }
                            }}
                            disabled={revertMut.isPending}
                            title="되돌리기 — 자동 산정으로 복귀"
                            className="btn btn-secondary btn-sm ml-1"
                          >
                            ↩
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                  {/*
                    분리 이어지는 행 — 건 공통 칸은 위 rowSpan 이 차지하므로 **분류·산정** 둘만 둔다.
                    (표가 rowSpan 으로 막힌 열을 건너뛰고 채우므로 이 두 칸이 제자리로 간다)
                  */}
                  {split?.slice(1).map((l, i) => (
                    <tr key={i}>
                      <td className={`px-2 py-1.5 align-top${i < split.length - 2 ? ' border-b border-slate-100' : ''}`}>
                        <CategoryCell row={r} line={l} />
                      </td>
                      <td className={`px-2 py-1.5 align-top${i < split.length - 2 ? ' border-b border-slate-100' : ''}`}>
                        <SplitDerivedItem r={r} line={l} isFirst={false} isLast={i === split.length - 2} />
                      </td>
                    </tr>
                  ))}
                  </tbody>
                );
              })}
            </table>
          </div>
          {/* 헤더가 정렬·필터 UI 로 바뀌며 ⓘ 안내를 여기로 옮김 (v2.79.1) */}
          <p className="px-3 py-2 border-t border-slate-100 text-xs text-slate-500 leading-snug">
            ※ 컬럼 제목 클릭 = 정렬 · 제목에 마우스를 올리면 🔍 필터.
            <b className="text-slate-500"> 납품내역(산정)</b>은 연결된 매입의 품목명 기준 자동 산정
            (매입연결이 없으면 제안품목 문구로 보조 산정).
            <b className="text-slate-500"> 용도</b>는 적어 두면 산정근거를 만들 때 그대로 따라가며,
            <b className="text-violet-700"> 스토리지 분류는 적지 않아도 기본 「영상저장」</b>입니다
            (직접 적은 값이 우선 · 칸을 비우면 기본값으로 돌아감).
          </p>
        </div>
      )}

      {purchasePopup && (
        <PurchasePopup row={purchasePopup} onClose={() => setPurchasePopup(null)} />
      )}
      {overrideModal && (
        <OverrideModal
          row={overrideModal.row}
          mode={overrideModal.mode}
          onSaved={refreshAfterOverride}
          onClose={() => setOverrideModal(null)}
        />
      )}
      {/* 영업번호·프로젝트명 클릭 → 영업건 요약 (보기 전용, v2.79.1) */}
      <Suspense fallback={null}>
        {salesSummaryId && (
          <SalesSummaryModal salesId={salesSummaryId} onClose={() => setSalesSummaryId(null)} />
        )}
      </Suspense>
    </div>
  );
}

/**
 * 산정 항목·수량 분리 표기 (v2.79.2) — 항목마다 한 줄: 이름(왼쪽) · 수량(오른쪽).
 *
 * ⭐이름과 수량을 **같은 격자 줄**에 둔다 — 칸을 둘(항목 칸·수량 칸)로 나눴더니
 *   긴 이름이 두 줄로 감기면 수량이 남의 항목 것으로 밀렸다(실측 20px 어긋남).
 * 항목이 없으면(원문 유지 등) 종전처럼 문자열 그대로 — 수량 없이.
 */
function DerivedLines({
  lines,
  fallback,
  nameCls = 'text-slate-800 font-medium',
  qtyCls = 'text-slate-500',
}: {
  lines: { name: string; qty: number }[];
  fallback: string;
  nameCls?: string;
  qtyCls?: string;
}) {
  if (lines.length === 0) {
    return <div className={`text-xs leading-5 ${nameCls}`}>{fallback}</div>;
  }
  return (
    <div className="grid grid-cols-[1fr_auto] gap-x-2 gap-y-0.5">
      {lines.map((l, i) => (
        // grid 의 한 줄 = 이름+수량 쌍 — 감김이 생겨도 쌍이 함께 늘어난다
        <div key={i} className="contents">
          <div className={`text-xs leading-5 ${nameCls}`}>{l.name}</div>
          <div className={`text-xs leading-5 text-right tabular-nums ${qtyCls}`}>
            {l.qty.toLocaleString('ko-KR')}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * 분류(추정) 칸 — **항목 하나에 분류 하나** (v2.79.5 사용자 08-21).
 *
 * 종전에는 납품 문구 한 줄 전체로 분류를 매겨, 서버 이름 뒤에 붙은 `Win11` 같은
 * 사양 표기까지 읽혀 한 행에 「서버 + 소프트웨어」가 같이 떴다. Win11 은 독립된
 * 납품 항목이 아니므로 그 분류를 붙이지 않는다.
 * 산정 항목이 아예 없는 행(부품·용역 등)만 종전대로 건 전체 분류를 보여준다.
 */
function CategoryCell({
  row,
  line,
}: {
  row: maintApi.BudgetDeliveryRow;
  line: { category: maintApi.BudgetCategory | null; excluded?: boolean } | null;
}) {
  if (row.service) {
    return (
      <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">용역(제외)</span>
    );
  }
  if (line !== null) {
    // 「단가표 작성」에서 「넣지 않음」으로 정한 품목 (v2.269.1) — 산정근거 어느 분류에도 안 들어간다
    if (line.excluded) {
      return (
        <span
          className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500"
          title="「단가표 작성」에서 「넣지 않음」으로 정한 품목 — 산정근거에 안 들어갑니다"
        >
          넣지 않음
        </span>
      );
    }
    return line.category === null ? (
      <span className="text-slate-400 text-xs">-</span>
    ) : (
      <span className={`text-xs px-1.5 py-0.5 rounded ${CAT_BADGE[line.category]}`}>
        {BUDGET_CATEGORY_LABEL[line.category]}
      </span>
    );
  }
  // 산정 항목이 없는 행 — 문구 기준 폴백(여럿일 수 있다). 자동채움도 같은 폴백을 쓴다.
  return (
    <div className="flex flex-wrap gap-1">
      {row.categories.length === 0 ? (
        <span className="text-slate-400 text-xs">-</span>
      ) : (
        row.categories.map((c) => (
          <span key={c} className={`text-xs px-1.5 py-0.5 rounded ${CAT_BADGE[c]}`}>
            {BUDGET_CATEGORY_LABEL[c]}
          </span>
        ))
      )}
    </div>
  );
}

/**
 * 건 분리 행의 산정 칸 (v2.79.3) — 항목 하나 + 건 공통 표식.
 *
 * 한 건에 항목이 여럿이면 항목마다 표의 행으로 나뉘는데, 배지(수정/제외)는 **첫 행**,
 * 건 전체에 대한 메모(사유·자동 산정값·산정 방법)는 **끝 행** 아래에 붙인다 —
 * 항목마다 반복해 적으면 항목별 사유처럼 읽힌다.
 */
function SplitDerivedItem({
  r,
  line,
  isFirst,
  isLast,
}: {
  r: maintApi.BudgetDeliveryRow;
  line: { name: string; qty: number };
  isFirst: boolean;
  isLast: boolean;
}) {
  const excluded = r.override?.action === 'exclude';
  const edited = r.override?.action === 'edit';
  return (
    <>
      {isFirst && excluded && (
        <div className="text-xs mb-0.5">
          <span className="inline-block px-1.5 py-0.5 rounded bg-rose-50 border border-rose-300 text-rose-700 text-xs">
            제외
          </span>
        </div>
      )}
      {isFirst && edited && (
        <div className="text-xs mb-0.5">
          <span className="inline-block px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-300 text-indigo-700 text-xs">
            수정
          </span>
        </div>
      )}
      <DerivedLines
        lines={[line]}
        fallback=""
        {...(excluded ? { nameCls: 'text-slate-400 line-through', qtyCls: 'text-slate-400 line-through' } : {})}
      />
      {isLast && (
        excluded ? (
          /* 사유는 제외에선 선택 (v2.79.6) — 비면 빈 줄을 만들지 않는다 */
          r.override!.reason === '' ? null : (
            <div className="text-xs text-rose-600 mt-0.5">사유: {r.override!.reason}</div>
          )
        ) : edited ? (
          <>
            <div className="text-xs text-indigo-600 mt-0.5">사유: {r.override!.reason}</div>
            {/* 무엇을 고쳤는지 화면에서 보이게 — 자동 산정값을 옆에 남긴다 */}
            <div className="text-xs text-slate-500 mt-0.5">
              자동: {r.override!.autoDerived === '' ? '(없음)' : r.override!.autoDerived}
            </div>
          </>
        ) : (
          <div className="text-xs text-slate-500 mt-0.5">
            {r.method}
            {r.derivedNote !== '' && <> · {r.derivedNote}</>}
          </div>
        )
      )}
    </>
  );
}

/**
 * 행 단위 「용도」 입력칸 (v2.77.4)
 *
 * 칸에 적고 밖을 누르면(또는 Enter) 저장된다 — 저장 버튼을 따로 두면 42행짜리 표에서
 * 어느 행을 저장 안 했는지 알 수 없다. 비우면 지워진다.
 * ⚠ 저장 후 목록을 새로고침하지 않는다 — 다른 행에 입력 중인 값이 날아가기 때문.
 *   서버 값과 화면 값이 같아서 새로고침이 필요 없다 (실패 시에만 원래 값으로 되돌린다).
 * ⚠ 대신 **호출부가 key 에 서버 값을 넣어** 목록을 다시 받으면 이 칸도 다시 맞춘다.
 *   그게 없으면 남이 채운 용도를 내 화면은 빈칸으로 들고 있다가 덮어 지운다.
 */
function UsageCell({ row }: { row: maintApi.BudgetDeliveryRow }) {
  const [value, setValue] = useState(row.usage ?? '');
  const [state, setState] = useState<'idle' | 'saving' | 'error'>('idle');
  const savedRef = { current: row.usage ?? '' };

  const save = async (v: string) => {
    const next = v.trim();
    if (next === (row.usage ?? '') && state !== 'error') return; // 안 바뀌면 안 보낸다
    setState('saving');
    try {
      await maintApi.saveDeliveryUsage({ salesId: row.salesId, legacyId: row.legacyId, usage: next });
      row.usage = next === '' ? null : next; // 캐시 행에도 반영 — 재렌더 시 초기값이 맞도록
      setState('idle');
    } catch {
      setState('error');
      setValue(savedRef.current); // 실패하면 원래 값으로 — 저장된 척하지 않는다
      window.alert('용도 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    }
  };

  return (
    <input
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={(e) => void save(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      disabled={state === 'saving'}
      placeholder="예) 영상저장"
      title={value !== '' ? value : '이 납품의 용도 — 산정근거에 따라갑니다'}
      className="ui-input ui-input-sm w-full"
    />
  );
}

/**
 * 수동 수정/제외 모달 (v2.77.3 · v2.79.3 구조화 입력)
 *
 * 자동 산정이 틀리게 읽은 행을 사람이 바로잡는다 — **수정은 사유 필수 · 제외는 선택**(v2.79.6).
 * v2.79.3: 서술형 한 줄 대신 **항목 이름·수량 칸**으로 받는다 (행 추가/삭제 가능).
 * 이름은 저장 전에 표준 표기 규칙을 한 번 통과한다(합계·단가표가 표준 표기 축이라서) —
 * 어떻게 정리되는지 입력하는 동안 미리보기로 보여준다. 저장과 같은 함수라 결과가 같다.
 */
function OverrideModal({
  row,
  mode,
  onSaved,
  onClose,
}: {
  row: maintApi.BudgetDeliveryRow;
  mode: 'edit' | 'exclude';
  onSaved: () => void;
  onClose: () => void;
}) {
  const isEdit = mode === 'edit';
  /**
   * 고치기 전 값 = 지금 화면에 뜨는 산정 항목들 (이미 수동 수정된 행이면 그 값).
   * lines 가 비면(용역·빈 행) 빈 칸 하나로 시작. 수량은 입력 중 상태라 문자열로 든다.
   */
  const [items, setItems] = useState<{ name: string; qty: string }[]>(() =>
    !isEdit || row.lines.length === 0
      ? [{ name: '', qty: '1' }]
      : row.lines.map((l) => ({ name: l.name, qty: String(l.qty) })),
  );
  const [reason, setReason] = useState(row.override?.reason ?? '');

  // 채워진 칸만 서버로 — 이름이 비면 무시, 수량·이름이 상한을 넘으면(서버 zod 와 같은 값) 그 행이 막는다
  const validItems = items
    .map((it) => ({ name: it.name.trim(), qty: Number(it.qty) }))
    .filter((it) => it.name !== '' && it.name.length <= 120
      && Number.isInteger(it.qty) && it.qty >= 1 && it.qty <= 99999);
  const invalidRow = (it: { name: string; qty: string }): boolean =>
    it.name.trim() !== ''
    && (it.name.trim().length > 120
      || !(Number.isInteger(Number(it.qty)) && Number(it.qty) >= 1 && Number(it.qty) <= 99999));
  const hasInvalid = items.some(invalidRow);

  // 미리보기는 타자 멈춘 뒤에만 — 글자마다 서버를 부르지 않는다 (deps 는 문자열 값)
  const validKey = JSON.stringify(validItems);
  const [previewKey, setPreviewKey] = useState('');
  useEffect(() => {
    if (!isEdit) return;
    const t = setTimeout(() => setPreviewKey(validKey), 400);
    return () => clearTimeout(t);
  }, [validKey, isEdit]);
  const preview = useQuery({
    queryKey: ['maintenance', 'overridePreviewItems', previewKey],
    queryFn: () => maintApi.previewDeliveryOverrideItems(
      JSON.parse(previewKey) as { name: string; qty: number }[],
    ),
    enabled: isEdit && previewKey !== '' && previewKey !== '[]',
    staleTime: 5 * 60_000,
  });

  const saveMut = useMutation({
    mutationFn: () =>
      maintApi.saveDeliveryOverride({
        salesId: row.salesId,
        legacyId: row.legacyId,
        action: mode,
        items: isEdit ? validItems : undefined,
        reason: reason.trim(),
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  /**
   * 저장 조건 — 사유는 **수정할 때만** 필수 (v2.79.6 사용자 08-21).
   * ⊘ 제외는 사유 없이도 누를 수 있다(서버 검사도 같은 규칙).
   */
  const canSave = (!isEdit || reason.trim() !== '')
    && (!isEdit || (validItems.length > 0 && !hasInvalid))
    && !saveMut.isPending;
  const autoValue = row.override !== null ? row.override.autoDerived : row.derived;

  return (
    <ModalShell
      title={
        isEdit
          ? `납품내역 수정 — ${row.salesId ?? `과거자료 #${row.legacyId}`}`
          : `납품내역 제외 — ${row.salesId ?? `과거자료 #${row.legacyId}`}`
      }
      onClose={onClose}
    >
      <div className="space-y-3 text-sm">
        <div className="text-xs text-slate-500">
          {row.projectName ?? '-'}
          {row.introDate !== null && <> · {row.datePrecision === 'month' ? row.introDate.slice(0, 7) : row.introDate}</>}
        </div>

        {row.item !== null && row.item !== '' && (
          <div className="text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2">
            <span className="text-slate-400">제안품목 원문</span>
            <div className="text-slate-700 mt-0.5">{row.item}</div>
          </div>
        )}
        <div className="text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2">
          <span className="text-slate-400">자동 산정</span>
          <div className="text-slate-700 mt-0.5">{autoValue === '' ? '(없음)' : autoValue}</div>
        </div>

        {isEdit ? (
          <div>
            <span className="text-slate-600 text-xs">
              수정할 납품내역 — <b>항목 이름과 수량</b>을 칸으로 적습니다 (이름은 저장 시 SM 내부 명칭으로 정리)
            </span>
            {/* 항목·수량 칸 (v2.79.3) — 행 추가/삭제로 여러 품목을 넣는다 */}
            <div className="mt-1 space-y-1">
              {items.map((it, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <input
                    type="text"
                    value={it.name}
                    maxLength={120}
                    onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                    className={`flex-1 border rounded px-2 py-1.5 text-sm ${
                      it.name.trim().length > 120 ? 'border-rose-400 bg-rose-50' : 'border-slate-300'
                    }`}
                    placeholder="예: IFT 16TB"
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    value={it.qty}
                    maxLength={5}
                    // 숫자만 — 붙여넣기에 섞인 문자는 걸러낸다. 상한은 서버 zod 와 같은 99,999
                    onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, qty: e.target.value.replace(/[^\d]/g, '') } : x)))}
                    className={`w-20 border rounded px-2 py-1.5 text-sm text-right tabular-nums ${
                      invalidRow(it) ? 'border-rose-400 bg-rose-50' : 'border-slate-300'
                    }`}
                    placeholder="수량"
                    title="수량 (1 ~ 99,999)"
                  />
                  <button
                    type="button"
                    onClick={() => setItems(items.filter((_, j) => j !== i))}
                    disabled={items.length === 1}
                    title={items.length === 1 ? '마지막 항목은 지울 수 없습니다' : '이 항목 빼기'}
                    className="btn btn-secondary btn-sm"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setItems([...items, { name: '', qty: '1' }])}
              disabled={items.length >= 50}
              title={items.length >= 50 ? '항목은 50개까지입니다' : undefined}
              className="btn btn-secondary btn-sm mt-1"
            >
              + 항목 추가
            </button>
            {hasInvalid && (
              <div className="mt-1 text-xs text-rose-600">수량은 1~99,999 사이 숫자 · 이름은 120자 이내로 적어 주세요.</div>
            )}
            {/* SM 내부 명칭 규칙을 거친 결과 — 저장되는 값은 이쪽이다 */}
            {preview.data && previewKey !== '' && previewKey === validKey && (
              preview.data.ok ? (
                <div className="mt-1 text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded px-2 py-1">
                  저장될 표기: <b>{preview.data.derived}</b>
                  {preview.data.note !== '' && <span className="text-teal-600"> · {preview.data.note}</span>}
                </div>
              ) : (
                <div className="mt-1 text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                  {/* 서버가 이유를 말해 주면 그 말로 (수량 상한 등) — 없으면 기본 안내 */}
                  {preview.data.note !== ''
                    ? preview.data.note
                    : '품목을 읽을 수 없습니다 — 행을 빼려면 [제외]를 써 주세요'}
                </div>
              )
            )}
            {/* 이름 한 칸에 여러 항목을 붙여넣은 의심 — 산정은 되지만 일부가 샐 수 있어 노랗게 알린다 */}
            {preview.data && previewKey !== '' && previewKey === validKey && preview.data.warn !== '' && (
              <div className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-300 rounded px-2 py-1">
                ⚠ {preview.data.warn}
              </div>
            )}
            {/* 미리보기 실패도 말한다 — 말없이 사라지면 어느 칸이 문제인지 알 수 없다 (검토 지적) */}
            {preview.isError && previewKey !== '' && previewKey === validKey && (
              <div className="mt-1 text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                미리보기 실패: {(preview.error as Error).message}
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">
            이 행을 <b>합계 · 산정근거 자동채움 · 납품항목 단가표</b>에서 뺍니다.
            화면 목록에는 회색으로 남아 언제든 되돌릴 수 있습니다.
          </div>
        )}

        <label className="block">
          <span className="text-slate-600 text-xs">
            {isEdit ? '수정' : '제외'} 사유{' '}
            {isEdit ? (
              <b className="text-rose-600">(필수)</b>
            ) : (
              /* ⊘ 제외는 사유를 안 적어도 된다 (v2.79.6 사용자 08-21) */
              <span className="text-slate-400">(선택 — 안 적어도 됩니다)</span>
            )}
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            className="ui-input ui-textarea mt-1 w-full"
            placeholder={isEdit ? '예: 실제 납품은 16TB 84장 (발주 문구가 옛 값)' : '예: 타사 장비 교체분이라 유지보수 대상 아님'}
          />
        </label>

        {saveMut.isError && (
          <div className="text-xs text-rose-600">{(saveMut.error as Error).message}</div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
          >
            취소
          </button>
          <button
            type="button"
            disabled={!canSave}
            onClick={() => saveMut.mutate()}
            className={`btn ${isEdit ? 'btn-primary' : 'btn-danger-solid'}`}
          >
            {saveMut.isPending ? '저장 중…' : isEdit ? '수정 저장' : '제외하기'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

/**
 * 연결된 매입내역 팝업 — 내용이 많아 표 칸에 다 못 넣는다 (사용자 지시: 클릭 → 팝업).
 *
 * 어떤 매입행이 산정에 반영됐는지( → 표기 )와 무시됐는지(규칙 밖)를 줄마다 보여준다 —
 * 납품내역 숫자가 어디서 왔는지 팝업만 보고 알 수 있어야 한다. ⚠금액은 서버가 안 준다.
 */
function PurchasePopup({
  row,
  onClose,
}: {
  row: maintApi.BudgetDeliveryRow;
  onClose: () => void;
}) {
  const mapped = row.purchases.filter((p) => p.mapped !== null);
  return (
    <ModalShell title={`연결된 매입내역 — ${row.salesId}`} onClose={onClose} wide>
      <p className="text-xs text-slate-500 mb-2">
        {row.projectName ?? '-'} · 매입 {row.purchases.length}건
        {mapped.length > 0 && (
          <>
            {' · 산정 반영 '}
            <b className="text-teal-700">{mapped.length}건</b>
          </>
        )}
      </p>
      {row.derived !== '' && (
        <div className="text-xs bg-teal-50 border border-teal-200 text-teal-900 rounded px-3 py-2 mb-3">
          납품내역(산정): <b>{row.derived}</b>
          <span className="text-teal-700"> · {row.method}</span>
        </div>
      )}
      <div className="max-h-[420px] overflow-y-auto border border-slate-200 rounded">
        <table className="w-full text-[13px]">
          <thead className="sticky top-0">
            <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-xs">
              <th className="text-left font-medium px-2 py-1.5 w-[80px]">품목그룹</th>
              <th className="text-left font-medium px-2 py-1.5 min-w-[130px]">품목명</th>
              <th className="text-left font-medium px-2 py-1.5 min-w-[160px]">규격</th>
              <th className="text-right font-medium px-2 py-1.5 w-[56px]">수량</th>
              <th className="text-left font-medium px-2 py-1.5 w-[130px]">산정 반영</th>
            </tr>
          </thead>
          <tbody>
            {row.purchases.map((p, i) => (
              <tr
                key={i}
                className={`border-b border-slate-100 last:border-0 ${
                  p.mapped === null ? 'text-slate-400' : 'text-slate-700'
                }`}
              >
                <td className="px-2 py-1.5 text-xs">{p.group || '-'}</td>
                <td className="px-2 py-1.5 text-xs font-medium">{p.name || '-'}</td>
                <td className="px-2 py-1.5 text-xs">{p.spec || '-'}</td>
                <td className="px-2 py-1.5 text-xs text-right tabular-nums">{p.qty}</td>
                <td className="px-2 py-1.5 text-xs">
                  {p.mapped === null ? (
                    <span className="text-slate-300">무시(규칙 밖)</span>
                  ) : (
                    <span className="text-teal-700">→ {p.mapped}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ModalShell>
  );
}

/* ═══ ③ 산정근거 만들기 ════════════════════════════════ */

function BasisView({
  customer,
  initialCategory,
  onBack,
}: {
  customer: string;
  initialCategory: BudgetCategory | null;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const [category, setCategory] = useState<BudgetCategory | null>(initialCategory);
  const [draft, setDraft] = useState<BudgetBasisItem[] | null>(null);
  const [note, setNote] = useState('');
  const [rate, setRate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const query = useQuery({
    queryKey: ['maintenance', 'budgetBasis', customer, category],
    queryFn: () => maintApi.getBudgetBasis(customer, category as BudgetCategory),
    enabled: category !== null,
    staleTime: 0,
  });

  // 요율은 빈 칸이면 null(=아직 안 정함). 0 과 다르다.
  const rateNum = rate.trim() === '' ? null : Number(rate);
  const rateValid = rateNum === null || (Number.isFinite(rateNum) && rateNum >= 0 && rateNum <= 100);

  /** 최종 산출 — 세 분류를 한 번에. 분류를 하나씩 열어 봐야 총액을 아는 건 산출이 아니다. */
  const summary = useQuery({
    queryKey: ['maintenance', 'budgetSummary', customer],
    queryFn: () => maintApi.getBudgetSummary(customer),
    staleTime: 0,
  });

  /**
   * 서버에서 온 내용을 편집용 초안으로 (분류가 바뀌거나 저장 후 다시 받으면 갱신).
   *
   * ⚠ **아직 안 만든 산정근거(exists=false)는 basisId·updatedAt 이 없어 키가 늘 같다.**
   *   그래서 납품내역에서 ⊘ 제외·✎ 수정·용도를 고치고 돌아와도 낡은 미리보기가
   *   그대로 떴다(머리말은 새 숫자, 표는 옛 행 — 같은 화면이 서로 다른 말을 했다).
   *   미리보기는 **읽기 전용**이라 지킬 사용자 편집이 없으므로, 내용이 바뀌면
   *   바로 따라가게 내용 자체를 키에 넣는다.
   */
  const serverKey = query.data?.exists === false
    ? `${category}|new|${JSON.stringify(query.data.items)}`
    : `${category}|${query.data?.basisId ?? 'new'}|${query.data?.updatedAt ?? ''}`;
  const [loadedKey, setLoadedKey] = useState('');
  /** 저장본 행의 지금 서버 값 — 문서 표기 ⚠ 를 초안이 아니라 이것으로(다른 화면에서 표준 표기를 이은 뒤에도 맞게) */
  const serverById = useMemo(
    () => new Map((query.data?.items ?? []).filter((x) => x.itemId != null).map((x) => [x.itemId as number, x])),
    [query.data],
  );
  if (query.data && loadedKey !== serverKey) {
    setLoadedKey(serverKey);
    setDraft(query.data.items);
    setNote(query.data.note ?? '');
    setRate(query.data.ratePercent === null ? '' : String(query.data.ratePercent));
    setSaved(false);
  }

  const createMut = useMutation({
    mutationFn: () => maintApi.createBudgetBasis(customer, category as BudgetCategory),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['maintenance', 'budgetBasis', customer, category] });
      qc.invalidateQueries({ queryKey: ['maintenance', 'budgetSummary', customer] });
      qc.invalidateQueries({ queryKey: ['maintenance', 'budgetCustomers'] });
    },
    onError: (e) => setError((e as Error).message),
  });

  const saveMut = useMutation({
    mutationFn: () =>
      maintApi.saveBudgetBasis(query.data!.basisId as number, {
        note: note || null,
        ratePercent: rateNum,
        items: draft ?? [],
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['maintenance', 'budgetBasis', customer, category] });
      qc.invalidateQueries({ queryKey: ['maintenance', 'budgetSummary', customer] });
      qc.invalidateQueries({ queryKey: ['maintenance', 'budgetCustomers'] });
    },
    onError: (e) => setError((e as Error).message),
  });

  const xlsxMut = useMutation({
    mutationFn: () => maintApi.downloadBudgetExcel(customer),
    onSuccess: () => setError(null),
    onError: (e) => setError((e as Error).message),
  });

  const refillMut = useMutation({
    mutationFn: () => maintApi.refillBudgetBasis(query.data!.basisId as number),
    onSuccess: (r) => {
      setError(null);
      setLoadedKey('');
      qc.invalidateQueries({ queryKey: ['maintenance', 'budgetBasis', customer, category] });
      qc.invalidateQueries({ queryKey: ['maintenance', 'budgetSummary', customer] });
      qc.invalidateQueries({ queryKey: ['maintenance', 'budgetCustomers'] });
      if (r.filledAmount === 0 && r.filledCapacity === 0) {
        setError('채울 수 있는 빈 칸이 없습니다 — 단가표에 없는 품목이거나 이미 값이 있습니다.');
      }
    },
    onError: (e) => setError((e as Error).message),
  });

  const deleteMut = useMutation({
    mutationFn: () => maintApi.deleteBudgetBasis(query.data!.basisId as number),
    onSuccess: () => {
      setError(null);
      setDraft(null);
      setLoadedKey('');
      qc.invalidateQueries({ queryKey: ['maintenance', 'budgetBasis', customer, category] });
      qc.invalidateQueries({ queryKey: ['maintenance', 'budgetSummary', customer] });
      qc.invalidateQueries({ queryKey: ['maintenance', 'budgetCustomers'] });
    },
    onError: (e) => setError((e as Error).message),
  });

  function patchRow(i: number, patch: Partial<BudgetBasisItem>) {
    setDraft((s) => (s ? s.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) : s));
    setSaved(false);
  }
  function removeRow(i: number) {
    setDraft((s) => (s ? s.filter((_, idx) => idx !== i) : s));
    setSaved(false);
  }
  function addRow() {
    setDraft((s) => [
      ...(s ?? []),
      { introDate: null, projectName: null, item: null, capacity: null, amount: null, sourceSalesId: null },
    ]);
    setSaved(false);
  }

  const exists = query.data?.exists ?? false;
  const totalAmount = (draft ?? []).reduce((s, r) => s + (r.amount ?? 0), 0);
  // 저장 전에도 요율을 치는 즉시 보이게 (저장하면 서버가 같은 식으로 다시 계산한다)
  const liveFee = rateValid ? budgetFee(totalAmount, rateNum) : null;

  return (
    <div className="space-y-3">
      <ViewHeader
        onBack={onBack}
        title={`산정근거 — ${customer}`}
        subtitle="분류를 고르면 해당 납품내역이 자동으로 채워집니다. 지우고 고쳐서 완성하세요."
        right={
          <button
            type="button"
            disabled={xlsxMut.isPending || !(summary.data?.categories ?? []).some((c) => c.exists)}
            onClick={() => xlsxMut.mutate()}
            title="이 고객사의 [요약] + 분류별 상세를 엑셀로 받습니다"
            className="btn btn-success"
          >
            {xlsxMut.isPending ? '만드는 중…' : '📊 엑셀 받기'}
          </button>
        }
      />

      {/* 최종 산출 — 세 분류를 늘 한눈에 */}
      <FinalSummary
        data={summary.data}
        loading={summary.isLoading}
        current={category}
        onPick={(c) => setCategory(c)}
      />

      {/* 분류 선택 */}
      <div className="flex items-center gap-2 flex-wrap">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCategory(c)}
            className={`btn ${category === c ? 'btn-primary' : 'btn-secondary'}`}
          >
            {BUDGET_CATEGORY_LABEL[c]}
          </button>
        ))}
      </div>

      {category === null && (
        <div className="bg-white rounded border border-slate-200 p-12 text-center text-slate-400">
          위에서 분류를 선택하세요 — 서버 · 스토리지 · 소프트웨어
        </div>
      )}

      {category !== null && query.isLoading && (
        <div className="bg-white rounded border border-slate-200 p-12 text-center text-slate-400"><LoadingLines /></div>
      )}
      {category !== null && query.isError && (
        <div className="bg-white rounded border border-rose-200 p-6 text-center text-rose-600">
          오류: {(query.error as Error).message}
        </div>
      )}

      {error && (
        <div className="text-sm bg-rose-50 border border-rose-200 text-rose-700 rounded px-3 py-2">
          {error}
        </div>
      )}

      {category !== null && query.data && (
        <>
          <div className="bg-indigo-50 border border-indigo-200 rounded px-4 py-2.5 text-xs text-slate-600 flex items-center justify-between flex-wrap gap-2">
            <span>
              {exists ? (
                <>
                  저장된 산정근거 · <b className="text-indigo-900">{(draft ?? []).length}행</b>
                  {query.data.updatedAt && (
                    <> · 마지막 저장 {query.data.updatedAt.slice(0, 16).replace('T', ' ')}</>
                  )}
                </>
              ) : (
                <>
                  아직 만들지 않았습니다 — 자동으로 채워질 후보{' '}
                  <b className="text-indigo-900">{query.data.items.length}행</b>
                </>
              )}
              {' · '}
              <span className="text-slate-500">
                분류 판정은 임시 규칙이라 넉넉히 걸립니다. 필요 없는 행은 지우세요. 품목은 납품내역과
                같은 SM 내부 명칭(예: SM 16TB → IFT 16TB)으로, 금액·용량은 「단가표 작성」의 그 이름 단가로
                자동으로 채웁니다. 최종 문서(엑셀) 품목 칸에는 「단가표 작성」에서 이어 둔
                <b className="text-teal-700"> 표준 표기(나라장터 쇼핑몰 등록 명칭)</b>로 적힙니다(품목 아래 📄 줄).
              </span>
            </span>
            <span className="flex items-center gap-2">
              {!exists && (
                <button
                  type="button"
                  disabled={createMut.isPending}
                  onClick={() => createMut.mutate()}
                  className="btn btn-primary"
                >
                  {createMut.isPending ? '불러오는 중…' : '📥 납품내역 불러오기'}
                </button>
              )}
              {exists && (
                <>
                  <button
                    type="button"
                    disabled={refillMut.isPending}
                    onClick={() => refillMut.mutate()}
                    title="비어 있는 도입금액·용량만 단가표를 보고 채웁니다(품목 글은 SM 내부 명칭으로 바꿔 찾음 — SM 16TB → IFT 16TB). 이미 값이 있는 칸은 건드리지 않습니다."
                    className="btn btn-warning btn-sm"
                  >
                    {refillMut.isPending ? '채우는 중…' : '💰 빈 칸 채우기'}
                  </button>
                  <button
                    type="button"
                    onClick={addRow}
                    className="btn btn-secondary btn-sm"
                  >
                    + 행 추가
                  </button>
                  <button
                    type="button"
                    disabled={saveMut.isPending}
                    onClick={() => saveMut.mutate()}
                    className="btn btn-primary"
                  >
                    {saveMut.isPending ? '저장 중…' : saved ? '✓ 저장됨' : '저장'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm('이 산정근거를 삭제하시겠습니까? (행 전체가 사라집니다)')) {
                        deleteMut.mutate();
                      }
                    }}
                    className="btn btn-danger btn-sm"
                  >
                    삭제
                  </button>
                </>
              )}
            </span>
          </div>

          <div className="bg-white rounded border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-xs">
                    <th className="text-left font-medium px-2 py-2 w-[130px]">도입날짜</th>
                    <th className="text-left font-medium px-2 py-2 min-w-[200px]">프로젝트명</th>
                    <th className="text-left font-medium px-2 py-2 min-w-[200px]">품목</th>
                    <th className="text-left font-medium px-2 py-2 w-[110px]">
                      용도
                      <span
                        className="ml-1 cursor-help text-slate-400"
                        title="납품내역 화면에 적어 둔 용도가 자동으로 실려 옵니다. 여기서 고칠 수도 있습니다."
                      >
                        ⓘ
                      </span>
                    </th>
                    <th className="text-left font-medium px-2 py-2 w-[110px]">용량</th>
                    <th className="text-right font-medium px-2 py-2 w-[130px]">도입금액</th>
                    {exists && <th className="w-[44px]" />}
                  </tr>
                </thead>
                <tbody>
                  {(draft ?? []).length === 0 && (
                    <tr>
                      <td colSpan={exists ? 7 : 6} className="px-3 py-10 text-center text-slate-400">
                        {exists ? '행이 없습니다. [+ 행 추가] 로 직접 넣을 수 있습니다.' : '해당하는 납품내역이 없습니다.'}
                      </td>
                    </tr>
                  )}
                  {(draft ?? []).map((r, i) => (
                    <tr key={i} className="border-b border-slate-100 last:border-0">
                      {exists ? (
                        <>
                          <td className="px-2 py-1">
                            <input
                              type="date"
                              value={r.introDate ?? ''}
                              onChange={(e) => patchRow(i, { introDate: e.target.value || null })}
                              className="ui-input ui-input-sm w-full tabular-nums"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              value={r.projectName ?? ''}
                              onChange={(e) => patchRow(i, { projectName: e.target.value || null })}
                              className="ui-input ui-input-sm w-full"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              value={r.item ?? ''}
                              // 고친 글의 문서 표기는 저장해야 다시 계산된다 — 옛 글의 📄 줄을 남겨 두면 다른 품목을 보여 준다
                              onChange={(e) => patchRow(i, {
                                item: e.target.value || null,
                                docLines: undefined, docConverted: undefined, noStandard: undefined, unconvertible: undefined,
                              })}
                              className="ui-input ui-input-sm w-full"
                            />
                            <UnpricedHint items={r.unpriced} />
                            <DoubleCountHint items={r.doubleCount} />
                            <UnclassifiedHint items={r.unclassified} />
                            {(() => {
                              // 문서 표기는 **지금 서버 값**으로 — 초안은 저장·분류가 바뀔 때만 다시 받아서, 다른 화면에서
                              //   표준 표기를 잇고 돌아와도 옛 ⚠ 가 남았다(독립 검토). 글을 안 고친 행만 서버 값을 쓴다.
                              const srv = r.itemId != null ? serverById.get(r.itemId) : undefined;
                              const h = srv !== undefined && srv.item === r.item ? srv : r;
                              return (
                                <DocNameHint docLines={h.docLines} docConverted={h.docConverted}
                                  noStandard={h.noStandard} unconvertible={h.unconvertible} />
                              );
                            })()}
                          </td>
                          <td className="px-2 py-1">
                            <input
                              value={r.usage ?? ''}
                              onChange={(e) => patchRow(i, { usage: e.target.value || null })}
                              placeholder="예) 영상저장"
                              className="ui-input ui-input-sm w-full"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              value={r.capacity ?? ''}
                              onChange={(e) => patchRow(i, { capacity: e.target.value || null })}
                              placeholder="예) 20TB"
                              className="ui-input ui-input-sm w-full"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              value={r.amount ?? ''}
                              inputMode="numeric"
                              onChange={(e) => {
                                const v = e.target.value.replace(/[^0-9]/g, '');
                                patchRow(i, { amount: v === '' ? null : Number(v) });
                              }}
                              placeholder="도입금액"
                              className="ui-input ui-input-sm w-full text-right tabular-nums"
                            />
                          </td>
                          <td className="px-2 py-1 text-center">
                            <button
                              type="button"
                              onClick={() => removeRow(i)}
                              title="이 행 삭제"
                              className="text-rose-400 hover:text-rose-600 text-sm"
                            >
                              ✕
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-1.5 tabular-nums text-slate-700 whitespace-nowrap">
                            {r.introDate ?? <span className="text-slate-300">미발행</span>}
                          </td>
                          <td className="px-2 py-1.5 text-slate-700">{r.projectName ?? '-'}</td>
                          <td className="px-2 py-1.5 text-slate-600">
                            {r.item ?? '-'}
                            <UnpricedHint items={r.unpriced} />
                            <DoubleCountHint items={r.doubleCount} />
                            <UnclassifiedHint items={r.unclassified} />
                            <DocNameHint docLines={r.docLines} docConverted={r.docConverted}
                              noStandard={r.noStandard} unconvertible={r.unconvertible} />
                          </td>
                          <td className="px-2 py-1.5 text-slate-600 text-xs">
                            {r.usage ?? <span className="text-slate-300">-</span>}
                          </td>
                          <td className="px-2 py-1.5 tabular-nums text-slate-700">
                            {r.capacity ?? <span className="text-slate-300">-</span>}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">
                            {r.amount === null ? (
                              <span className="text-slate-300">-</span>
                            ) : (
                              r.amount.toLocaleString('ko-KR')
                            )}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
                {exists && (draft ?? []).length > 0 && (
                  <tfoot>
                    <tr className="bg-slate-50 border-t border-slate-200 font-medium text-slate-700">
                      <td colSpan={5} className="px-2 py-2 text-right text-xs">
                        도입금액 합계
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        {totalAmount.toLocaleString('ko-KR')}
                      </td>
                      <td />
                    </tr>
                    {/* 요율 → 유지보수료. 도입금액 합계 바로 밑에 둬야 무엇에 몇 %인지 보인다. */}
                    <tr className="bg-slate-50 border-t border-slate-200">
                      <td colSpan={5} className="px-2 py-2 text-right text-xs text-slate-600">
                        <span className="mr-2">유지보수 요율</span>
                        <input
                          value={rate}
                          inputMode="decimal"
                          placeholder="예) 8"
                          onChange={(e) => {
                            setRate(e.target.value.replace(/[^0-9.]/g, ''));
                            setSaved(false);
                          }}
                          className={`w-20 border rounded px-1.5 py-1 text-xs text-right tabular-nums ${
                            rateValid ? 'border-slate-300' : 'border-rose-400 bg-rose-50'
                          }`}
                        />
                        <span className="ml-1">%</span>
                        <span className="ml-3 text-slate-400">
                          연간 유지보수료 = 도입금액 합계 × 요율
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-bold text-teal-700">
                        {!rateValid ? (
                          <span className="text-rose-600 text-xs">0~100 사이</span>
                        ) : liveFee === null ? (
                          <span className="text-amber-600 text-xs font-normal">요율 미정</span>
                        ) : (
                          won(liveFee)
                        )}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {exists && (
            <label className="ui-label block">
              <span className="text-slate-600 text-xs">메모</span>
              <textarea
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                  setSaved(false);
                }}
                rows={2}
                placeholder="산정 근거에 대한 설명·전제 등"
                className="ui-input ui-textarea mt-1 w-full"
              />
            </label>
          )}
        </>
      )}
    </div>
  );
}

/**
 * 산정 규칙 문서 패널 (v2.77.5)
 *
 * 연결된 매입내역·제안품목이 어떤 기준으로 표준 표기로 통합되는지 — 내용은
 * 판정 엔진의 같은 표에서 온다(서버 /budget/derive-rules). 고정 표준 표기 옆에는
 * 단가 칸이 붙어 규칙을 보면서 바로 넣고 고칠 수 있다.
 * 용량 패턴(IFT 00TB·EDS 00TB)은 이름이 하나가 아니라, 지금 납품 목록에 실제로
 * 있는 용량들을 찾아 같이 보여준다.
 */
function DeriveRulesPanel({
  rules,
  loading,
  items,
  unused,
  onSavePrice,
}: {
  rules: { steps: string[]; sections: maintApi.DeriveRuleSection[] } | null;
  loading: boolean;
  items: maintApi.DeliveryPriceItem[];
  unused: maintApi.UnusedPriceItem[];
  onSavePrice: (label: string, v: number | null) => void;
}) {
  // 단가 잇기 — 목록에 나오는 품목(items.key)과 아직 안 나오는 단가(unused.matchKey) 둘 다에서 찾는다
  const priceByKey = useMemo(() => {
    const m = new Map<string, number | null>();
    for (const u of unused) m.set(u.matchKey, u.unitPrice);
    for (const it of items) m.set(it.key, it.unitPrice); // 목록 값이 이긴다 (같은 키면 같은 값이긴 하다)
    return m;
  }, [items, unused]);

  if (loading || rules === null) {
    return (
      <div className="bg-white rounded border border-indigo-200 p-6 text-center text-slate-400 text-sm">
        규칙을 불러오는 중…
      </div>
    );
  }

  return (
    <div className="bg-white rounded border border-indigo-200 overflow-hidden">
      <div className="px-3 py-2 bg-indigo-50 border-b border-indigo-200 text-sm font-medium text-indigo-900">
        📖 납품내역 통합 규칙 — 매입내역·제안품목이 SM 내부 명칭이 되는 기준
        <span className="ml-2 text-xs font-normal text-indigo-600">
          (화면 설명이 아니라 실제 판정 엔진의 규칙표 그대로입니다)
        </span>
      </div>

      <div className="p-3 space-y-3 text-sm">
        {/* 우선순위 — 위에서부터 차례로 */}
        <ol className="space-y-1 text-xs text-slate-600 list-none">
          {rules.steps.map((s, i) => (
            <li key={i} className="pl-1">{s}</li>
          ))}
        </ol>

        {rules.sections.map((sec) => {
          // 용량 패턴 계열 — 지금 목록에 실제로 있는 그 접두의 표준 디스크
          const family = sec.familyPrefix === null
            ? []
            : items.filter(
                (it) => it.standard && it.kind === 'disk' && it.label.startsWith(`${sec.familyPrefix} `),
              );
          return (
            <div key={sec.title} className="border border-slate-200 rounded overflow-hidden">
              <div className="px-2.5 py-1.5 bg-slate-50 border-b border-slate-200">
                <span className="text-xs font-bold text-slate-700">{sec.title}</span>
                {sec.note !== null && (
                  <div className="text-xs text-slate-500 mt-0.5">{sec.note}</div>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <tbody>
                    {sec.entries.map((e, i) => (
                      <tr key={i} className="border-b border-slate-100 last:border-0">
                        <td className="px-2.5 py-1.5 text-slate-600 min-w-[220px]">{e.from}</td>
                        <td className="px-1 py-1.5 text-slate-300 w-[24px] text-center">→</td>
                        <td className="px-2 py-1.5 font-medium text-slate-800 min-w-[150px] whitespace-nowrap">
                          {e.to}
                        </td>
                        <td className="px-2 py-1 w-[140px] text-right">
                          {e.priceLabel !== null && e.matchKey !== null && (
                            <NumberCell
                              value={priceByKey.get(e.matchKey) ?? null}
                              placeholder="단가 미정"
                              onCommit={(v) => onSavePrice(e.priceLabel!, v)}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                    {family.length > 0 && (
                      <tr>
                        <td colSpan={4} className="px-2.5 py-2 bg-slate-50/60">
                          <div className="text-xs text-slate-500 mb-1.5">
                            지금 납품 목록에 있는 용량별 표기 — 여기서 바로 단가를 넣을 수 있습니다
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {family.map((f) => (
                              <span
                                key={f.key}
                                className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-slate-300 bg-white"
                              >
                                <span className="text-slate-700">{f.label}</span>
                                <span className="w-[104px]">
                                  <NumberCell
                                    value={f.unitPrice}
                                    placeholder="단가 미정"
                                    onCommit={(v) => onSavePrice(f.label, v)}
                                  />
                                </span>
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * 새 품목 단가 등록 (v2.77.4)
 *
 * 목록은 납품내역에서 자동으로 나오지만, 아직 납품 전인 품목의 단가를 미리
 * 넣어 둘 수도 있어야 한다. 같은 표기가 이미 있으면 서버가 그 줄을 고친다
 * (같은 물건의 단가가 두 줄이 되면 어느 쪽이 맞는지 알 수 없으니).
 */
function NewPriceModal({ onSaved, onClose }: { onSaved: () => void; onClose: () => void }) {
  const [label, setLabel] = useState('');
  const [price, setPrice] = useState('');
  const [cap, setCap] = useState('');
  const mut = useMutation({
    mutationFn: () =>
      /**
         ⚠ **빈 칸은 아예 안 보낸다**(null 이 아니라 undefined).
         서버는 값이 오면 "그 값으로 바꾸기"라 null 을 보내면 **지우기**가 된다.
         같은 표기가 이미 등록돼 있을 때 단가를 비운 채 용량만 넣으면 등록해 둔
         단가가 소리 없이 사라진다(실측: 45,000,000원이 NULL 이 됐다).
         지우려면 목록의 그 칸을 비우면 된다 — 여기는 '새로 넣기'다.
      */
      maintApi.saveDeliveryPrice({
        label: label.trim(),
        ...(price === '' ? {} : { unitPrice: Number(price) }),
        ...(cap === '' ? {} : { capacityTb: Number(cap) }),
      }),
    onSuccess: (r) => {
      onSaved();
      onClose();
      if (!r.created) {
        window.alert(
          '같은 표기가 이미 있어 그 줄에 값을 채웠습니다.\n' +
            '(비워 둔 칸은 원래 값을 그대로 두었습니다)',
        );
      }
    },
  });
  const canSave = label.trim() !== '' && !mut.isPending;

  return (
    <ModalShell title="새 품목 단가 등록" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <label className="block">
          <span className="text-slate-600 text-xs">품목 표기 <b className="text-rose-600">(필수)</b> — 납품내역 산정 표기와 같은 이름이어야 자동으로 연결됩니다</span>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="예) IFT 16TB · X 5U84 · ES-620P"
            className="ui-input mt-1 w-full"
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-slate-600 text-xs">1개당 도입금액(원)</span>
            <input
              value={price}
              inputMode="numeric"
              onChange={(e) => setPrice(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="비우면 미정"
              className="ui-input mt-1 w-full text-right tabular-nums"
            />
          </label>
          <label className="block">
            <span className="text-slate-600 text-xs">1개당 용량(TB)</span>
            <input
              value={cap}
              inputMode="decimal"
              onChange={(e) => setCap(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="이름에 TB 가 있으면 비우세요"
              className="ui-input mt-1 w-full text-right tabular-nums"
            />
          </label>
        </div>
        {mut.isError && (
          <div className="text-xs text-rose-600">{(mut.error as Error).message}</div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
          >
            취소
          </button>
          <button
            type="button"
            disabled={!canSave}
            onClick={() => mut.mutate()}
            className={`btn ${canSave ? 'btn-success-solid' : 'btn-secondary'}`}
          >
            {mut.isPending ? '저장 중…' : '등록'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

/* ═══ 납품항목 단가표 (v2.72.0) ════════════════════════ */

const DELIVERY_KIND_LABEL: Record<maintApi.DeliveryPriceItem['kind'], string> = {
  body: '본체',
  server: '서버',
  disk: '디스크',
};
const DELIVERY_KIND_BADGE: Record<maintApi.DeliveryPriceItem['kind'], string> = {
  body: 'bg-violet-100 text-violet-700',
  server: 'bg-sky-100 text-sky-700',
  disk: 'bg-teal-100 text-teal-700',
};

/** 사람이 분류를 골랐나 (넣지 않음 포함) */
function isPicked(c: maintApi.DeliveryPriceItem['category']): boolean {
  return c === 'server' || c === 'storage' || c === 'software' || c === 'none';
}
/** 분류 미정 — 안 골랐고 규칙으로도 안 나온다 → 산정근거에 「⚠ 분류 미정」 (v2.269.1) */
function isUnclassified(it: maintApi.DeliveryPriceItem): boolean {
  return !isPicked(it.category) && (it.autoCategory ?? null) === null;
}

/**
 * 품목 분류 고르기 (v2.269.1 · 사용자 「품목 이름마다 분류 선택」) — 자동(규칙) · 서버 · 스토리지 · 소프트웨어 · 넣지 않음.
 * 고르면 모든 고객사·모든 건의 산정근거와 납품내역 분류 표시에 같이 적용된다.
 */
function CategoryPick({
  item,
  disabled,
  onPick,
}: {
  item: maintApi.DeliveryPriceItem;
  disabled: boolean;
  onPick: (c: maintApi.BudgetCategory | 'none' | null) => void;
}) {
  const auto = item.autoCategory ?? null;
  const value = isPicked(item.category) ? (item.category as string) : '';
  const unclassified = isUnclassified(item);
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => {
        const v = e.target.value;
        onPick(v === '' ? null : (v as maintApi.BudgetCategory | 'none'));
      }}
      title={unclassified ? '규칙으로 분류가 안 나옵니다 — 골라야 산정근거에 들어갑니다' : '이 품목을 넣을 산정근거 분류'}
      className={`ui-input ui-input-sm w-full ${unclassified ? 'border-violet-400 text-violet-700' : ''}`}
      data-category-pick
    >
      {/* 칸 폭(140px)에 들어가게 짧게 — 「자동 (소프트웨어)」 84px 가 가장 길다(1280 실측) */}
      <option value="">{auto === null ? '⚠ 미정' : `자동 (${BUDGET_CATEGORY_LABEL[auto]})`}</option>
      <option value="server">서버</option>
      <option value="storage">스토리지</option>
      <option value="software">소프트웨어</option>
      <option value="none">넣지 않음</option>
    </select>
  );
}

/**
 * 산정 납품항목별 1개당 도입금액.
 *
 * 목록은 **저장된 표가 아니라 영업건에서 매번 다시 센 것**이다 — 지울 수 없고,
 * 새 납품이 생기면 저절로 나타난다. 사람이 매기는 것은 금액·용량뿐.
 *
 * ⚠ 금액 칸이 비어 있는 것과 0원은 다르다. 빈 칸 = 아직 안 정함.
 */
function DeliveryPricesView({ onBack }: { onBack: () => void }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [scope, setScope] = useState<'standard' | 'all'>('standard');
  const [onlyUnpriced, setOnlyUnpriced] = useState(false);
  /** 분류 미정만 (v2.269.1) — 사람이 안 골랐고 규칙으로도 분류가 안 나오는 품목 */
  const [onlyUnclassified, setOnlyUnclassified] = useState(false);
  /** 표준 표기 없는 것만 (v2.316.1) — 최종 문서에 SM 내부 명칭 그대로 나갈 품목 */
  const [onlyNoStandard, setOnlyNoStandard] = useState(false);
  /** [＋ 표준 표기 잇기] · [바꾸기] → 고르기 창 (v2.316.1) — 누른 품목의 key */
  const [stdPickKey, setStdPickKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** [+ 새 품목] — 납품내역에 아직 없는 표기를 미리 등록할 때 (v2.77.4) */
  const [adding, setAdding] = useState(false);
  /** [📖 산정 규칙] — 매입·문구가 어떤 기준으로 SM 내부 명칭이 되는지 (v2.77.5) */
  const [showRules, setShowRules] = useState(false);
  /**
   * 「영업건」 숫자 클릭 → 그 품목이 나온 영업건 목록 (v2.316.0 · 사용자 「몇 건 → 무슨 건인지」).
   * ⭐ 누른 때의 목록을 들고 있지 않고 **무엇을 눌렀는지만** 둔다 — 창이 열린 채 단가표를 새로 받아도
   *   (창 포커스 · 납품내역 수정) 아래 salesListView 가 지금 목록에서 다시 찾아 숫자와 목록이 같이 바뀐다.
   */
  const [salesList, setSalesList] = useState<
    | { kind: 'item'; key: string }
    | { kind: 'suggest'; priceId: number; label: string }
    | null
  >(null);
  /** 그 목록에서 영업번호 클릭 → 영업건 요약 (보기 전용 · 납품내역 보기와 같은 창) */
  const [salesSummaryId, setSalesSummaryId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['maintenance', 'deliveryPrices'],
    queryFn: maintApi.listDeliveryPrices,
    staleTime: 0,
  });

  const rulesQuery = useQuery({
    queryKey: ['maintenance', 'deriveRules'],
    queryFn: maintApi.getDeriveRules,
    enabled: showRules,
    staleTime: 5 * 60_000, // 규칙은 배포로만 바뀐다
  });

  function invalidate() {
    setError(null);
    qc.invalidateQueries({ queryKey: ['maintenance', 'deliveryPrices'] });
    qc.invalidateQueries({ queryKey: ['maintenance', 'budgetPrices'] });
    // v2.268.1 부터 이 표의 단가가 산정근거 금액에, v2.269.1 부터 분류가 산정근거·납품내역 분류에 쓰인다
    qc.invalidateQueries({ queryKey: ['maintenance', 'budgetBasis'] });
    qc.invalidateQueries({ queryKey: ['maintenance', 'budgetSummary'] });
    qc.invalidateQueries({ queryKey: ['maintenance', 'budgetDeliveries'] });
  }

  const saveMut = useMutation({
    mutationFn: (v: {
      label: string; unitPrice?: number | null; capacityTb?: number | null;
      category?: maintApi.BudgetCategory | 'none' | null;
      standardName?: string | null; standardG2bNo?: string | null;
    }) => maintApi.saveDeliveryPrice(v),
    onSuccess: invalidate,
    onError: (e) => setError((e as Error).message),
  });

  const delMut = useMutation({
    mutationFn: (id: number) => maintApi.deleteDeliveryPrice(id),
    onSuccess: invalidate,
    onError: (e) => setError((e as Error).message),
  });

  const xlsxMut = useMutation({
    mutationFn: () => maintApi.downloadDeliveryPriceExcel(),
    onError: (e) => setError((e as Error).message),
  });

  const all = query.data?.items ?? [];
  const unused = query.data?.unused ?? [];
  const summary = query.data?.summary;

  /** 지금 목록에서 다시 찾은 「영업건」 창 내용 — 그 품목이 목록에서 빠졌으면 null */
  const salesListView = useMemo(() => {
    if (salesList === null) return null;
    if (salesList.kind === 'item') {
      const it = (query.data?.items ?? []).find((x) => x.key === salesList.key);
      return it
        ? {
            title: `영업건 ${it.rows}건 — ${it.label}`,
            note: `매입연결 ${it.fromPurchase}건 · 문구 ${it.fromText}건`,
            refs: (it.refs ?? []).map((r) => ({ key: r.key, qty: r.qty as number | null })),
          }
        : null;
    }
    const u = (query.data?.unused ?? []).find((x) => x.priceId === salesList.priceId);
    const s = u?.suggest.find((x) => x.label === salesList.label);
    return u && s
      ? {
          title: `같은 영업건 ${s.rows}건 — ${u.label} → ${s.label}`,
          note: `제안품목 글에 「${u.label}」 · 납품내역(산정)에 「${s.label}」 — 둘이 같이 나온 건`,
          refs: (s.refs ?? []).map((key) => ({ key, qty: null as number | null })),
        }
      : null;
  }, [salesList, query.data]);
  const kw = q.trim().toLowerCase();
  // 값을 매길 것이 목적이므로 **빈칸을 위로** 올린다 (그 안에서는 서버 정렬 = 많이 나온 순)
  const items = all
    .filter(
      (it) =>
        (scope === 'all' || it.standard) &&
        (!onlyUnpriced || it.unitPrice === null) &&
        (!onlyUnclassified || isUnclassified(it)) &&
        // 이을 수 없는 품목(영문·숫자 없음 · 쉼표·괄호가 든 이름)은 이어도 안 바뀌어 「할 일」이 아니다
        (!onlyNoStandard || (standardLinkable(it) && !it.standardName)) &&
        (!kw || it.label.toLowerCase().includes(kw)),
    )
    .map((it, i) => ({ it, i }))
    .sort((a, b) => {
      const av = a.it.unitPrice === null ? 0 : 1;
      const bv = b.it.unitPrice === null ? 0 : 1;
      return av - bv || a.i - b.i;
    })
    .map((x) => x.it);

  return (
    <div className="space-y-3">
      <ViewHeader
        onBack={onBack}
        title="품목 단가표"
        subtitle="납품내역으로 정리한 SM 내부 명칭마다 1개당 도입금액·분류·표준 표기(나라장터 쇼핑몰 등록 명칭)를 정해 둡니다."
        right={
          <div className="flex items-center gap-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="검색"
              className="ui-input w-28"
            />
            <button
              type="button"
              onClick={() => setShowRules((v) => !v)}
              title="연결된 매입내역·제안품목이 어떤 기준으로 SM 내부 명칭으로 통합되는지 — 규칙마다 단가를 바로 넣을 수 있습니다"
              className={`btn ${showRules ? 'btn-outline' : 'btn-outline'}`}
            >
              📖 산정 규칙
            </button>
            <button
              type="button"
              onClick={() => setAdding(true)}
              title="목록에 아직 없는 품목의 단가를 새로 등록합니다 (같은 표기가 있으면 그 줄을 고칩니다)"
              className="btn btn-success"
            >
              + 새 품목
            </button>
            <button
              type="button"
              disabled={xlsxMut.isPending}
              onClick={() => xlsxMut.mutate()}
              title="화면과 같은 목록을 엑셀로 — [SM 내부 명칭만] 시트가 따로 있습니다"
              className="btn btn-success"
            >
              {xlsxMut.isPending ? '만드는 중…' : '📊 엑셀'}
            </button>
          </div>
        }
      />

      {adding && (
        <NewPriceModal
          onSaved={invalidate}
          onClose={() => setAdding(false)}
        />
      )}

      {error && (
        <div className="text-sm bg-rose-50 border border-rose-200 text-rose-700 rounded px-3 py-2">
          {error}
        </div>
      )}

      {query.isLoading && (
        <div className="bg-white rounded border border-slate-200 p-12 text-center text-slate-400"><LoadingLines /></div>
      )}

      {summary && (
        <>
          {/*
            ⚠ v2.57.2 의 교훈 — '화면에 칸이 있다'가 '동작한다'는 뜻은 아니다.
            v2.268.1 부터 여기 단가가 산정근거 금액에, v2.269.1 부터 분류가 산정근거 분류에 실제로 쓰인다
            (전엔 「아직 안 쓰인다」고 적혀 있었다 — 바뀐 뒤에도 그대로 남아 있던 거짓 안내를 고침).
          */}
          <div className="text-xs bg-sky-50 border border-sky-200 text-sky-800 rounded px-3 py-2">
            여기 넣은 <b>단가</b>로 산정근거의 도입금액을 채우고, <b>분류</b>(서버·스토리지·소프트웨어·넣지 않음)로
            그 품목이 어느 산정근거에 들어갈지 정합니다 — 모든 고객사·모든 건에 같이 적용됩니다.
            「자동」은 규칙이 정한 분류이고, 규칙으로도 안 나오는 품목은 산정근거에 「⚠ 분류 미정」으로 뜹니다.
            이미 만들어 둔 산정근거는 [💰 빈 칸 채우기]로 금액을, 삭제 후 [📥 납품내역 불러오기]로 분류까지 다시 맞춥니다.
            <br />
            <b>납품항목 → SM 내부 명칭(IFT 16TB 등) → 표준 표기(나라장터 쇼핑몰 등록 명칭)</b> — 품목마다
            [＋ 표준 표기 잇기]로 나라장터 명칭을 이어 두면, 산정근거 <b>최종 문서(엑셀) 품목 칸</b>에 SM 내부 명칭 대신
            그 이름이 적힙니다(이미 만든 산정근거도 바로 · 안 이은 품목은 SM 내부 명칭 그대로 + ⚠).
          </div>

          <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2">
            영업건 <b>{summary.salesRows}건</b>에서 납품항목 <b>{summary.total}종</b>을 찾았습니다 —
            그중 단가가 있는 것 <b>{summary.priced}종</b>,{' '}
            <b className="text-rose-700">아직 없는 것 {summary.unpriced}종</b>.
            {' '}규칙으로 정리된 <b>SM 내부 명칭 {summary.standardTotal}종</b> 중에는{' '}
            <b>{summary.standardPriced}종</b>만 값이 있습니다.
            {summary.standardLinked !== undefined && (
              <> 표준 표기(나라장터)를 이은 것 <b className="text-teal-700">{summary.standardLinked}종</b>.</>
            )}
            {summary.hiddenCustomers > 0 && (
              <> · 목록에서 제외한 고객사 {summary.hiddenCustomers}곳은 빼고 셌습니다.</>
            )}
            {(summary.unissuedRows ?? 0) > 0 && (
              <> · 계산서 미발행 영업건 {summary.unissuedRows}건도 뺐습니다.</>
            )}
            {(summary.manualExcluded ?? 0) > 0 && (
              <> · 납품내역에서 수동 제외한 {summary.manualExcluded}건도 뺐습니다.</>
            )}
          </div>

          {showRules && (
            <DeriveRulesPanel
              rules={rulesQuery.data ?? null}
              loading={rulesQuery.isLoading}
              items={all}
              unused={unused}
              onSavePrice={(label, v) => saveMut.mutate({ label, unitPrice: v })}
            />
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <FilterChip
              active={scope === 'standard'}
              onClick={() => setScope('standard')}
              label={`SM 내부 명칭 (${all.filter((i) => i.standard).length})`}
            />
            <FilterChip
              active={scope === 'all'}
              onClick={() => setScope('all')}
              label={`원문 포함 전체 (${all.length})`}
            />
            <label className="flex items-center gap-1.5 text-xs text-slate-500 ml-1">
              <input
                type="checkbox"
                checked={onlyUnpriced}
                onChange={(e) => setOnlyUnpriced(e.target.checked)}
              />
              단가 없는 것만
            </label>
            <label className="flex items-center gap-1.5 text-xs text-slate-500 ml-1" data-only-unclassified>
              <input
                type="checkbox"
                checked={onlyUnclassified}
                onChange={(e) => setOnlyUnclassified(e.target.checked)}
              />
              분류 미정만{summary.unclassifiedKinds !== undefined && ` (${summary.unclassifiedKinds})`}
            </label>
            {summary.standardLinked !== undefined && (
              <label className="flex items-center gap-1.5 text-xs text-slate-500 ml-1" data-only-no-standard>
                <input
                  type="checkbox"
                  checked={onlyNoStandard}
                  onChange={(e) => setOnlyNoStandard(e.target.checked)}
                />
                표준 표기 없는 것만 ({all.filter((it) => standardLinkable(it) && !it.standardName).length})
              </label>
            )}
            <span className="text-xs text-slate-500 ml-auto">{items.length}종 표시 중</span>
          </div>

          <div className="bg-white rounded border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-xs">
                    <th className="text-left font-medium px-3 py-2 min-w-[200px]">납품항목</th>
                    <th className="text-left font-medium px-2 py-2 w-[70px]">종류</th>
                    <th
                      className="text-left font-medium px-2 py-2 w-[140px] min-w-[140px]"
                      title="이 품목을 산정근거의 어느 분류에 넣을지 — 모든 고객사·모든 건에 같이 적용"
                    >
                      분류
                    </th>
                    <th className="text-right font-medium px-2 py-2 w-[80px]">영업건</th>
                    <th className="text-right font-medium px-2 py-2 w-[80px]">총 수량</th>
                    <th className="text-right font-medium px-2 py-2 w-[140px]">1개당 도입금액</th>
                    <th className="text-right font-medium px-2 py-2 w-[100px]">1개당 용량</th>
                    <th className="text-left font-medium px-2 py-2 w-[70px]">출처</th>
                    <th className="w-[40px]" />
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-3 py-10 text-center text-slate-400">
                        해당하는 납품항목이 없습니다.
                      </td>
                    </tr>
                  )}
                  {items.map((it) => (
                    <tr key={it.key} className="border-b border-slate-100 last:border-0">
                      <td className="px-3 py-1.5">
                        <span className={it.standard ? 'text-slate-800' : 'text-slate-500'}>
                          {it.label}
                        </span>
                        {!it.standard && (
                          <span className="ml-1.5 text-xs text-slate-500">원문 그대로</span>
                        )}
                        <div className="text-xs text-slate-500 mt-0.5">
                          매입연결 {it.fromPurchase}건 · 문구 {it.fromText}건
                          {it.customers.length > 0 && <> · {it.customers.join(', ')}</>}
                        </div>
                        {it.nameWarn && (
                          <div className="text-xs text-amber-600 mt-0.5">
                            매입 품목명이 정리 안 됐습니다 — 이 표기 그대로 단가가 붙습니다
                          </div>
                        )}
                        {it.note && (
                          <div className="text-xs text-amber-700 mt-0.5">{it.note}</div>
                        )}
                        {/* 표준 표기(나라장터 쇼핑몰 등록 명칭 · v2.316.1) — 최종 문서 품목 칸에 이 이름이 적힌다 · 옛 서버면 칸째 없음 */}
                        {it.standardName !== undefined && (
                          <StandardNameLine
                            item={it}
                            disabled={saveMut.isPending}
                            onPick={() => setStdPickKey(it.key)}
                          />
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        {/*
                          ⚠ 규칙에 안 걸린 원문 항목은 **종류를 모른다**. 엔진이 내부 정렬용으로
                            'server' 를 붙일 뿐이라 그대로 배지를 달면 'DDR4 32GB — 서버',
                            'Vmware — 서버' 처럼 틀린 것을 아는 척하게 된다.
                        */}
                        {it.standard ? (
                          <span
                            className={`inline-block px-1.5 py-0.5 rounded text-xs ${DELIVERY_KIND_BADGE[it.kind]}`}
                          >
                            {DELIVERY_KIND_LABEL[it.kind]}
                          </span>
                        ) : (
                          <span className="text-slate-300" title="규칙이 없어 종류를 모릅니다">
                            -
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1 min-w-[140px]">
                        <CategoryPick
                          item={it}
                          disabled={saveMut.isPending}
                          onPick={(category) => saveMut.mutate({ label: it.label, category })}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">
                        {/* 옛 서버(refs 없음)면 숫자를 글자로만 둔다 */}
                        {it.refs && it.refs.length > 0 ? (
                          // 클릭 → 이 품목이 나온 영업건 목록 (v2.316.0)
                          <button
                            type="button"
                            data-item-sales={it.key}
                            onClick={() => setSalesList({ kind: 'item', key: it.key })}
                            title="클릭 → 이 품목이 나온 영업건 보기"
                            className="underline decoration-dotted underline-offset-2 hover:decoration-solid hover:text-blue-700"
                          >
                            {it.rows}
                          </button>
                        ) : (
                          it.rows
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">
                        {it.qty.toLocaleString('ko-KR')}
                      </td>
                      <td className="px-2 py-1">
                        <NumberCell
                          value={it.unitPrice}
                          placeholder="미정"
                          onCommit={(v) => saveMut.mutate({ label: it.label, unitPrice: v })}
                        />
                      </td>
                      <td className="px-2 py-1 text-right">
                        {/*
                          ⚠ 이름에 TB 가 있으면 계산이 **이름의 TB 를 먼저 쓴다**.
                            여기 넣어 봐야 안 쓰이는데 넣었다고 믿게 되는 게 더 나쁘고,
                            장비 등록용량을 넣으면 디스크와 이중 계산된다(96TB→208TB 사고).
                        */}
                        {it.capacityFromName !== null ? (
                          <span
                            className="text-xs text-slate-500 tabular-nums"
                            title="품목 이름에 적힌 용량을 씁니다 — 따로 넣을 필요가 없습니다"
                          >
                            {it.capacityFromName}TB
                          </span>
                        ) : (
                          <NumberCell
                            value={it.capacityTb}
                            placeholder="TB"
                            allowDecimal
                            onCommit={(v) => saveMut.mutate({ label: it.label, capacityTb: v })}
                          />
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-xs text-slate-500">
                        {/* 출처는 단가의 출처 — 표준 표기·분류만 정한 줄(v2.316.1)에 「직접」을 띄우면 단가를 넣은 줄로 읽힌다 */}
                        {it.source === null || it.unitPrice === null
                          ? ''
                          : it.source === 'g2b'
                          ? '나라장터'
                          : '직접'}
                        {it.fromTextTable && (
                          <div className="text-[11px] text-slate-500">문구표</div>
                        )}
                      </td>
                      <td className="px-2 py-1 text-center">
                        {it.priceId !== null && !it.fromTextTable && (
                          <button
                            type="button"
                            onClick={() => {
                              // v2.316.1 — 이 줄에 표준 표기도 있다(한 줄에 단가·분류·표준 표기) → 같이 지워진다고 알린다
                              if (window.confirm(
                                it.standardName
                                  ? `'${it.label}' 의 단가·분류·표준 표기를 지울까요?\n(표준 표기를 지우면 최종 문서에 SM 내부 명칭 그대로 적힙니다)`
                                  : `'${it.label}' 의 단가를 지울까요?`,
                              )) {
                                delMut.mutate(it.priceId!);
                              }
                            }}
                            title="이 품목의 단가·분류·표준 표기 삭제 (목록에서는 안 없어집니다 · 분류는 「자동」으로)"
                            className="text-rose-400 hover:text-rose-600 text-sm"
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/*
            단가표엔 있는데 지금 산정 목록엔 안 나오는 것.
            ⭐ 지우지 않고 보여 준다 — 규칙 표기를 바꾸면 채워 둔 금액이 아무 경고 없이
              여기로 떨어진다. 안 보이면 "분명히 넣었는데 사라졌다"가 된다.
            ⭐ [이 금액 넣기] 는 **복사**다(옮기기가 아니다) — 옛 이름 줄을 지우면
              지금 돌아가는 자동채움이 그 자리에서 금액을 잃는다. 더하기만 한다.
          */}
          {unused.length > 0 && (
            <div className="bg-white rounded border border-slate-200 overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs text-slate-600">
                이름이 안 맞는 단가 <b className="text-slate-900">{unused.length}개</b> — 단가표에는
                있지만 산정된 납품항목 중에는 같은 표기가 없습니다. 나라장터는 <b>우리 모델명</b>,
                산정 표기는 <b>매입 품목명</b>이라 다릅니다. 같은 장비가 맞으면 [이 금액 넣기]를
                누르세요 (옛 줄은 그대로 둡니다).
              </div>
              <table className="w-full text-[13px]">
                <tbody>
                  {unused.map((u) => (
                    <tr key={u.priceId} className="border-b border-slate-100 last:border-0 align-top">
                      <td className="px-3 py-2 text-slate-600 min-w-[130px]">{u.label}</td>
                      <td className="px-2 py-2 w-[130px] text-right tabular-nums text-slate-500">
                        {u.unitPrice === null ? (
                          <span className="text-slate-300">미정</span>
                        ) : (
                          `${u.unitPrice.toLocaleString('ko-KR')}원`
                        )}
                      </td>
                      <td className="px-2 py-2 w-[70px] text-xs text-slate-500">
                        {u.source === 'g2b' ? '나라장터' : '직접'}
                      </td>
                      <td className="px-2 py-2">
                        {u.suggest.length === 0 ? (
                          <span className="text-xs text-slate-400">같이 나온 SM 내부 명칭 없음</span>
                        ) : (
                          <div className="flex flex-col gap-1">
                            {u.suggest.map((s) => (
                              <div key={s.label} className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs text-slate-500">→</span>
                                <span className="text-xs text-slate-700">{s.label}</span>
                                <span className="text-xs text-slate-500">
                                  같은 영업건{' '}
                                  {s.refs && s.refs.length > 0 ? (
                                    // 클릭 → 같이 나온 영업건 목록 (v2.316.0)
                                    <button
                                      type="button"
                                      data-suggest-sales={s.label}
                                      onClick={() => setSalesList({ kind: 'suggest', priceId: u.priceId, label: s.label })}
                                      title="클릭 → 같이 나온 영업건 보기"
                                      className="underline decoration-dotted underline-offset-2 hover:decoration-solid hover:text-blue-700"
                                    >
                                      {s.rows}건
                                    </button>
                                  ) : (
                                    <>{s.rows}건</>
                                  )}
                                  에서 같이 나옴
                                </span>
                                {u.unitPrice !== null && (
                                  <button
                                    type="button"
                                    disabled={saveMut.isPending}
                                    onClick={() =>
                                      saveMut.mutate({ label: s.label, unitPrice: u.unitPrice })
                                    }
                                    title={`'${s.label}' 에 ${u.unitPrice.toLocaleString('ko-KR')}원을 넣습니다. '${u.label}' 줄은 그대로 둡니다.`}
                                    className="btn btn-success btn-sm"
                                  >
                                    이 금액 넣기
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {stdPickKey !== null && (() => {
        const target = all.find((x) => x.key === stdPickKey);
        return target ? (
          <StandardNamePicker
            item={target}
            onSave={async (name, g2bNo) => {
              await saveMut.mutateAsync({ label: target.label, standardName: name, standardG2bNo: g2bNo });
              setStdPickKey(null);
            }}
            onClose={() => setStdPickKey(null)}
          />
        ) : null;
      })()}

      {salesList && (
        <ItemSalesPopup
          title={salesListView?.title ?? '영업건 목록'}
          note={salesListView?.note ?? '지금 단가표 목록에 이 품목이 없습니다 — 단가표가 새로 바뀌었습니다'}
          refs={salesListView?.refs ?? []}
          onOpenSales={setSalesSummaryId}
          onClose={() => setSalesList(null)}
        />
      )}
      {/* 목록 위에 뜬다 — 같은 z 라 늦게 그린 쪽이 위 (납품내역 보기와 같은 요약 창) */}
      <Suspense fallback={null}>
        {salesSummaryId && (
          <SalesSummaryModal salesId={salesSummaryId} onClose={() => setSalesSummaryId(null)} />
        )}
      </Suspense>
    </div>
  );
}

/**
 * 단가표 「영업건」 목록 (v2.316.0 · 사용자 「영업건 몇 건 표시되는 부분을 누르면 그 건이 무슨 건인지」).
 *
 * ⭐ 어느 건인지는 숫자와 같은 계산에서 나온다 — 서버가 품목을 셀 때 그 건의 열쇠(key)를 같이 적어 보낸다(refs).
 *   화면이 따로 다시 세면 「12건」이라 해 놓고 11줄을 보여 주는 일이 생긴다.
 *   이름·날짜는 창을 열 때 받는다(단가표 목록마다 실으면 응답이 세 배) — 단가표를 새로 받으면 이것도(같은 key 앞머리).
 * 보기만 하는 창이라 ESC 로 닫힌다. 영업번호를 누르면 납품내역 보기와 같은 영업건 요약이 위에 뜬다.
 * ⚠ 금액은 없다 — 서버가 건명·날짜·고객사만 싣는다.
 */
function ItemSalesPopup({
  title,
  note,
  refs,
  onOpenSales,
  onClose,
}: {
  title: string;
  note: string;
  /** qty = 그 건에서의 이 품목 수량 · null 이면 수량 칸을 안 그린다(「같이 나온 건」 목록) */
  refs: { key: string; qty: number | null }[];
  onOpenSales: (salesId: string) => void;
  onClose: () => void;
}) {
  useDialogLayer(true, onClose);
  const query = useQuery({
    // ['maintenance','deliveryPrices'] 앞머리 — 단가표·수동 수정이 그 key 를 새로 받으면 이것도 같이
    queryKey: ['maintenance', 'deliveryPrices', 'sources'],
    queryFn: maintApi.listDeliveryPriceSources,
    // 열 때마다 새로(받아 둔 것은 바로 보이고 뒤에서 바꾼다) — 단가표 목록도 늘 새로 받으므로,
    //   여기만 오래 들고 있으면 방금 생긴 건이 「못 찾았다」로 뜬다
    staleTime: 0,
  });
  const byKey = useMemo(
    () => new Map((query.data?.sources ?? []).map((s) => [s.key, s])),
    [query.data],
  );
  // 날짜 내림차순(없는 날짜는 뒤로) — 납품내역 보기와 같은 순서
  const list = refs
    .flatMap((r) => {
      const src = byKey.get(r.key);
      return src === undefined ? [] : [{ key: r.key, qty: r.qty, src }];
    })
    .sort((a, b) => {
      const ad = a.src.introDate;
      const bd = b.src.introDate;
      if (ad !== bd) {
        if (ad === null) return 1;
        if (bd === null) return -1;
        return bd.localeCompare(ad);
      }
      return a.key.localeCompare(b.key);
    });
  const showQty = refs.some((r) => r.qty !== null);
  const totalQty = list.reduce((s, x) => s + (x.qty ?? 0), 0);
  const customers = new Set(list.map((x) => x.src.customer)).size;
  // 받아 둔 것이 있으면 다시 받다 실패해도 그것을 보여 준다(아래 한 줄로 알림) — 처음부터 없을 때만 이 칸
  if (!query.data) {
    return (
      <ModalShell title={title} onClose={onClose} wide>
        <div className={`text-sm py-10 text-center ${query.isError ? 'text-rose-600' : 'text-slate-400'}`}>
          {query.isError ? `목록을 못 불러왔습니다: ${(query.error as Error).message}` : '불러오는 중…'}
        </div>
      </ModalShell>
    );
  }
  return (
    <ModalShell title={title} onClose={onClose} wide>
      <p className="text-xs text-slate-500 mb-2" data-item-sales-popup={list.length}>
        {note} · 고객사 {customers}곳
        {showQty && <> · 총 수량 {totalQty.toLocaleString('ko-KR')}개</>}
        {' '}· 영업번호를 누르면 그 건 요약이 열립니다.
      </p>
      {/*
        목록이 숫자보다 짧으면 숨기지 않고 알린다 — 조용히 빠지면 「12건」 옆에 11줄이 남는다.
        새로 받는 중에는 띄우지 않는다(방금 생긴 건이 잠깐 비는 것 — 받고 나면 채워진다).
      */}
      {!query.isFetching && list.length < refs.length && (
        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-1.5 mb-2">
          {refs.length - list.length}건은 목록을 찾지 못했습니다 — 화면을 새로고침해 보세요.
        </div>
      )}
      {query.isError && (
        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-1.5 mb-2">
          목록을 새로 받지 못해 앞서 받은 것을 보여 줍니다 — {(query.error as Error).message}
        </div>
      )}
      <div className="max-h-[420px] overflow-y-auto border border-slate-200 rounded">
        <table className="w-full text-[13px]">
          <thead className="sticky top-0">
            <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-xs">
              <th className="text-left font-medium px-2 py-1.5 w-[110px]">고객사</th>
              <th className="text-left font-medium px-2 py-1.5 w-[86px]">도입날짜</th>
              <th className="text-left font-medium px-2 py-1.5 w-[104px]">영업번호</th>
              <th className="text-left font-medium px-2 py-1.5 min-w-[160px]">프로젝트명</th>
              {showQty && <th className="text-right font-medium px-2 py-1.5 w-[52px]">수량</th>}
              <th className="text-left font-medium px-2 py-1.5 w-[92px]">산정</th>
            </tr>
          </thead>
          <tbody>
            {list.map(({ key, qty, src }) => (
              <tr key={key} className="border-b border-slate-100 last:border-0 align-top">
                <td className="px-2 py-1.5 text-xs text-slate-700">{src.customer}</td>
                <td className="px-2 py-1.5 text-xs tabular-nums text-slate-600 whitespace-nowrap">
                  {/* 과거자료엔 년월만 아는 행이 있다 — 1일로 채운 날짜를 그대로 쓰면 없는 정밀도가 생긴다 */}
                  {src.introDate === null
                    ? '-'
                    : src.datePrecision === 'month'
                      ? src.introDate.slice(0, 7)
                      : src.introDate}
                </td>
                <td className="px-2 py-1.5 text-xs tabular-nums whitespace-nowrap">
                  {src.salesId !== null ? (
                    <button
                      type="button"
                      onClick={() => onOpenSales(src.salesId!)}
                      title="클릭 → 이 영업건 정보 보기"
                      className="text-slate-500 underline decoration-dotted underline-offset-2 hover:decoration-solid hover:text-blue-700"
                    >
                      {src.salesId}
                    </button>
                  ) : (
                    <span
                      className="inline-block px-1.5 py-0.5 rounded bg-amber-50 border border-amber-300 text-amber-700 text-xs whitespace-nowrap"
                      title="영업진행에 없어 기초자료 엑셀에서 가져온 과거 납품입니다"
                    >
                      과거자료
                    </span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-xs text-slate-600">{src.projectName ?? '-'}</td>
                {showQty && (
                  <td className="px-2 py-1.5 text-xs text-right tabular-nums">
                    {qty === null ? '-' : qty.toLocaleString('ko-KR')}
                  </td>
                )}
                <td className="px-2 py-1.5 text-xs text-slate-500">{src.method}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ModalShell>
  );
}

/**
 * 📄 표준 표기 매칭 (v2.316.1 · 사용자 「단가표 작성처럼 SM 내부 명칭과 표준 명칭을 매칭시키는 기능 · 추천 매칭도」).
 *
 *   납품항목 → SM 내부 명칭(IFT 16TB) → 표준 표기(나라장터 쇼핑몰 등록 명칭)
 *
 * 단가표 작성과 **같은 목록**(서버 /budget/delivery-prices — SM 내부 명칭은 늘 영업건에서 다시 센 것)에
 * 줄마다 지금 이은 표준 표기 · 추천(lib/budget-standard) · [추천대로] [고르기] [끊기].
 * [✔ 확실한 추천 N건 한 번에 잇기] — 안 이은 품목 중 「확실」(디스크 = 단가·용량·회사 · 그 밖 = 단가·모델 이름 글자 그대로 ·
 *   그런 후보가 하나뿐)만 · 확인 창에 **전부** 보인다. 가격만 같은 것은 다른 제품일 수 있어 사람이 보고 [추천대로]·[고르기]로 잇는다.
 *   이미 이은 것은 건드리지 않는다(서버도 안 이은 줄만 잇고, 그새 이어진 것은 건너뛰었다고 알려 준다).
 * 이은 이름은 산정근거 최종 문서(엑셀) 품목 칸에 SM 내부 명칭 대신 적힌다(이미 만든 산정근거도 바로).
 */
function StandardMatchView({ onBack }: { onBack: () => void }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [scope, setScope] = useState<'standard' | 'all'>('standard');
  const [onlyUnlinked, setOnlyUnlinked] = useState(false);
  const [onlyRecommended, setOnlyRecommended] = useState(false);
  /** [고르기] → 고르기 창 — 누른 품목의 key */
  const [pickKey, setPickKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['maintenance', 'deliveryPrices'],
    queryFn: maintApi.listDeliveryPrices,
    staleTime: 0,
  });
  const namesQ = useQuery({
    queryKey: ['maintenance', 'standardNames'],
    queryFn: maintApi.listStandardNames,
    staleTime: 60_000,
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['maintenance', 'deliveryPrices'] });
    // 표준 표기는 산정근거 화면 📄 줄·최종 산출 ⚠ 에도 쓰인다
    qc.invalidateQueries({ queryKey: ['maintenance', 'budgetBasis'] });
    qc.invalidateQueries({ queryKey: ['maintenance', 'budgetSummary'] });
  }

  const saveMut = useMutation({
    mutationFn: (v: { label: string; standardName: string | null; standardG2bNo: string | null }) =>
      maintApi.saveDeliveryPrice({ label: v.label, standardName: v.standardName, standardG2bNo: v.standardG2bNo }),
    onSuccess: () => { setError(null); setNotice(null); invalidate(); },
    onError: (e) => setError((e as Error).message),
  });
  const bulkMut = useMutation({
    mutationFn: (items: { label: string; standardName: string; standardG2bNo: string | null }[]) =>
      maintApi.saveStandardNames(items),
    onSuccess: (r) => {
      setError(null);
      setConfirmBulk(false);
      // 그새 다른 곳에서 이어진 것은 서버가 건드리지 않고 돌려준다 — 사람이 고른 연결을 추천이 덮지 않게
      const skipped = r.skipped ?? [];
      setNotice(`확실한 추천 ${r.updated + r.created}건을 이었습니다 — 산정근거 최종 문서 품목 칸에 바로 반영됩니다.` +
        (skipped.length > 0 ? ` 그새 이미 이어진 ${skipped.length}건(${skipped.join(', ')})은 그대로 뒀습니다.` : ''));
      invalidate();
    },
    onError: (e) => setError((e as Error).message),
  });
  /** [✔ 한 번에 잇기] → 잇는 것 **전부**를 보여 주는 확인 창 (확실은 사람이 하나하나 안 보고 이어지므로) */
  const [confirmBulk, setConfirmBulk] = useState(false);

  const rows = useMemo(() => {
    const names = namesQ.data?.names ?? [];
    return (query.data?.items ?? []).map((it) => ({ it, rec: recommendStandard(it, names) }));
  }, [query.data, namesQ.data]);
  // 영문·숫자가 없는 품목(서버랙 등)은 이을 수 없다(서버도 거절) — 세지 않고 줄에도 단추를 안 둔다
  const linkableRows = rows.filter((r) => standardLinkable(r.it));
  const linked = linkableRows.filter((r) => r.it.standardName).length;
  /** 한 번에 잇기 대상 — 안 이은 품목의 확실한 추천만 */
  const sureTodo = linkableRows.filter((r) => !r.it.standardName && r.rec.sure && r.rec.best !== null);
  const kw = q.trim().toLowerCase();
  const shown = rows
    .filter((r) =>
      (scope === 'all' || r.it.standard) &&
      (!onlyUnlinked || (standardLinkable(r.it) && !r.it.standardName)) &&
      (!onlyRecommended || r.rec.best !== null) &&
      (!kw || r.it.label.toLowerCase().includes(kw) || (r.it.standardName ?? '').toLowerCase().includes(kw)))
    // 안 이은 것 → 확실한 추천 → 서버 순서(많이 나온 순)
    .map((r, i) => ({ r, i }))
    .sort((a, b) =>
      Number(!!a.r.it.standardName) - Number(!!b.r.it.standardName) ||
      Number(b.r.rec.sure) - Number(a.r.rec.sure) ||
      a.i - b.i)
    .map((x) => x.r);
  const busy = saveMut.isPending || bulkMut.isPending;
  const pickTarget = pickKey === null ? undefined : rows.find((r) => r.it.key === pickKey)?.it;
  const loading = query.isPending || namesQ.isPending;
  // 못 불러오면(옛 서버 404 · 권한 403 · 500) 빈 화면 대신 까닭을 보인다
  const loadError = (query.isError && !query.data ? query.error : null) ?? (namesQ.isError && !namesQ.data ? namesQ.error : null);

  return (
    <div className="space-y-3">
      <ViewHeader
        onBack={onBack}
        title="표준 표기 매칭"
        subtitle="SM 내부 명칭마다 표준 표기(나라장터 쇼핑몰 등록 명칭)를 이어 둡니다 — 산정근거 최종 문서(엑셀) 품목 칸에 이 이름이 적힙니다(앞의 품명은 빼고)."
        right={
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="검색"
            className="ui-input w-36"
          />
        }
      />

      <div className="text-xs bg-sky-50 border border-sky-200 text-sky-800 rounded px-3 py-2">
        <b>납품항목 → SM 내부 명칭(IFT 16TB 등) → 표준 표기(나라장터 쇼핑몰 등록 명칭)</b> — 후보는 나라장터쇼핑몰 메뉴의
        MAS 제품 · 3자단가 등록 명칭입니다. <b className="text-emerald-700">확실</b>은 디스크면 단가·용량·회사가 모두 같고, 그 밖은
        단가와 모델 이름이 글자 그대로 같으며, 그런 후보가 하나뿐인 것입니다(한 번에 잇기 대상). <b>추천</b>은 용량·회사(디스크)나
        모델 이름이 맞는 것, <b>가격만 같음</b>은 다른 제품일 수 있으니 보고 [추천대로]·[고르기]로 이어 주세요.
        문서는 품목 글이 <b>이은 SM 내부 명칭과 글자 그대로 같은 품목만</b> 바꿉니다 — 한 칸 안에서도 이은 품목만 바뀌고,
        안 이은 품목은 SM 내부 명칭 그대로 남으며 ⚠ 로 알립니다. 문서엔 나라장터 명칭 앞의 <b>품명</b>
        (하드디스크드라이브·디스크어레이·컴퓨터서버 등)을 빼고, 수량은 SM 내부 명칭처럼 <b>「* 7」</b>로 같은 칸에 적습니다.
        나라장터에 등록 명칭이 없는 품목은 [고르기] → 「나라장터 등록 명칭 없음」으로 정해 두면 ⚠ 가 사라집니다.
      </div>

      {error && (
        <div className="text-sm bg-rose-50 border border-rose-200 text-rose-700 rounded px-3 py-2">{error}</div>
      )}
      {notice && (
        <div className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-3 py-2">{notice}</div>
      )}

      {loading && !loadError && (
        <div className="bg-white rounded border border-slate-200 p-12 text-center text-slate-400"><LoadingLines /></div>
      )}
      {loadError && (
        <div className="bg-white rounded border border-rose-200 p-8 text-center text-sm text-rose-700" data-std-load-error>
          목록을 못 불러왔습니다: {(loadError as Error).message}
          <div className="mt-3">
            <button
              type="button"
              onClick={() => { void query.refetch(); void namesQ.refetch(); }}
              className="btn btn-outline btn-sm"
            >
              다시 불러오기
            </button>
          </div>
        </div>
      )}

      {query.data && namesQ.data && (
        <>
          <div className="flex items-center gap-3 flex-wrap bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2 text-xs" data-std-summary>
            <span>
              이을 수 있는 품목 <b>{linkableRows.length}종</b> · 표준 표기를 이은 것 <b className="text-teal-700">{linked}종</b> ·
              안 이은 것 <b className="text-rose-700">{linkableRows.length - linked}종</b> ·
              그중 확실한 추천 <b className="text-emerald-700">{sureTodo.length}종</b>
              {rows.length > linkableRows.length && (
                <span
                  className="text-slate-500"
                  title="영문·숫자가 없는 품목(서버랙 등) · 쉼표·괄호가 들었거나 60자가 넘는 이름은 산정근거 문서에서 표준 표기로 바꿀 수 없어 글 그대로 적힙니다"
                >
                  {' '}· 이을 수 없는 품목 {rows.length - linkableRows.length}종(영문·숫자 없음 · 쉼표·괄호가 든 이름 — 문서엔 글 그대로)
                </span>
              )}
            </span>
            <button
              type="button"
              disabled={busy || sureTodo.length === 0}
              data-std-bulk={sureTodo.length}
              onClick={() => setConfirmBulk(true)}
              className="btn btn-success btn-sm ml-auto"
              title="안 이은 품목 중 확실한 추천만 — 가격만 같은 것은 빠집니다"
            >
              {bulkMut.isPending ? '잇는 중…' : `✔ 확실한 추천 ${sureTodo.length}건 한 번에 잇기`}
            </button>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <FilterChip
              active={scope === 'standard'}
              onClick={() => setScope('standard')}
              label={`SM 내부 명칭 (${rows.filter((r) => r.it.standard).length})`}
            />
            <FilterChip
              active={scope === 'all'}
              onClick={() => setScope('all')}
              label={`원문 포함 전체 (${rows.length})`}
            />
            <label className="flex items-center gap-1.5 text-xs text-slate-500 ml-1">
              <input type="checkbox" checked={onlyUnlinked} onChange={(e) => setOnlyUnlinked(e.target.checked)} />
              안 이은 것만
            </label>
            <label className="flex items-center gap-1.5 text-xs text-slate-500 ml-1">
              <input type="checkbox" checked={onlyRecommended} onChange={(e) => setOnlyRecommended(e.target.checked)} />
              추천 있는 것만
            </label>
            <span className="text-xs text-slate-500 ml-auto">{shown.length}종 표시 중</span>
          </div>

          <div className="bg-white rounded border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]" data-std-table>
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-xs">
                    <th className="text-left font-medium px-3 py-2 w-[22%] min-w-[170px]">SM 내부 명칭</th>
                    <th className="text-left font-medium px-2 py-2 w-[34%]">표준 표기 (문서에 적히는 나라장터 명칭)</th>
                    <th className="text-left font-medium px-2 py-2">추천</th>
                    <th className="w-[150px]" />
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-3 py-10 text-center text-slate-400">해당하는 품목이 없습니다.</td>
                    </tr>
                  )}
                  {shown.map(({ it, rec }) => {
                    const best = rec.best;
                    const same = best !== null && best.candidate.name === it.standardName;
                    const linkable = standardLinkable(it);
                    const badge = best === null ? null : standardBadge(best, rec.sure);
                    return (
                      <tr key={it.key} className="border-b border-slate-100 last:border-0 align-top" data-std-row={it.key}>
                        <td className="px-3 py-1.5">
                          <span className={it.standard ? 'text-slate-800' : 'text-slate-500'}>{it.label}</span>
                          {!it.standard && <span className="ml-1.5 text-xs text-slate-500">원문 그대로</span>}
                          <div className="text-xs text-slate-500 mt-0.5">
                            {it.standard && <>{DELIVERY_KIND_LABEL[it.kind]} · </>}영업건 {it.rows} ·{' '}
                            {it.unitPrice === null ? '단가 미정' : `${it.unitPrice.toLocaleString('ko-KR')}원`}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-xs">
                          {!linkable ? (
                            <span className="text-slate-400" data-std-unlinkable={it.key}>{standardUnlinkableReason(it)}</span>
                          ) : standardIsSelf(it) ? (
                            <span className="text-teal-800">📄 나라장터 등록 명칭 없음 — 문서엔 품목 글 그대로</span>
                          ) : it.standardName ? (
                            <span className="text-teal-800 break-words" title={`나라장터 등록 명칭: ${it.standardName}`}>
                              📄 {standardDocName(it.standardName)}
                              {it.standardG2bNo && <span className="text-slate-400"> · {it.standardG2bNo}</span>}
                            </span>
                          ) : (
                            <span className="text-slate-400">— 안 이음 (문서엔 품목 글 그대로 + ⚠)</span>
                          )}
                          {linkable && standardLabelDiffers(it) && (
                            <div className="text-amber-700 mt-0.5" data-std-label-differs={it.key}>
                              ⚠ 「{it.standardLabel}」 이름으로 이어 둔 것 — 글자가 다른 지금 이름의 품목 글은 안 바뀜([고르기]에서 다시 저장)
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-xs">
                          {!linkable ? null : best === null ? (
                            <span className="text-slate-300">추천 없음 — [고르기]로 찾거나 직접 적기</span>
                          ) : same ? (
                            <span className="text-emerald-700">✓ 추천과 같음</span>
                          ) : (
                            <>
                              <span className="text-slate-700 break-words" title={`나라장터 등록 명칭: ${best.candidate.name}`}>
                                {standardDocName(best.candidate.name)}
                              </span>
                              <span
                                className={`ml-1.5 inline-block px-1.5 py-0.5 rounded text-[11px] border ${
                                  badge === 'sure'
                                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                                    : badge === 'recommend'
                                      ? 'bg-amber-50 border-amber-300 text-amber-700'
                                      : 'bg-slate-50 border-slate-300 text-slate-600'
                                }`}
                                title={rec.second ? `2등: ${standardDocName(rec.second.candidate.name)} (${rec.second.why.join(' · ')})` : '다른 후보 없음'}
                                data-std-sure={rec.sure ? '1' : '0'}
                              >
                                {badge === 'sure' ? '확실' : badge === 'recommend' ? '추천' : '가격만 같음 — 모델 확인'} · {best.why.join(' · ')}
                              </span>
                            </>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          {/* 단추가 셋(추천대로·고르기·끊기)이면 150px 을 넘는다 — 줄을 바꿔 쌓아 1280 화면에서도 표가 안 넘치게 */}
                          <div className="flex flex-wrap justify-end gap-1">
                          {linkable && best !== null && !same && (
                            <button
                              type="button"
                              disabled={busy}
                              data-std-accept={it.key}
                              onClick={() => saveMut.mutate({
                                label: it.label, standardName: best.candidate.name, standardG2bNo: best.candidate.g2bNo,
                              })}
                              className="btn btn-success btn-sm"
                              title={`'${standardDocName(best.candidate.name)}' 로 잇습니다`}
                            >
                              추천대로
                            </button>
                          )}
                          {linkable && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => setPickKey(it.key)}
                              className="btn btn-outline btn-sm"
                              title="후보 목록에서 고르거나 직접 적습니다 · 나라장터에 없으면 「나라장터 등록 명칭 없음」"
                            >
                              고르기
                            </button>
                          )}
                          {linkable && it.standardName && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => {
                                if (window.confirm(`'${it.label}' 의 표준 표기 연결을 끊을까요?\n최종 문서에 SM 내부 명칭 그대로 적힙니다.`)) {
                                  saveMut.mutate({ label: it.label, standardName: null, standardG2bNo: null });
                                }
                              }}
                              className="btn btn-secondary btn-sm"
                              title="연결 끊기 — 문서엔 SM 내부 명칭 그대로"
                            >
                              끊기
                            </button>
                          )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {pickTarget && (
        <StandardNamePicker
          item={pickTarget}
          onSave={async (name, g2bNo) => {
            await saveMut.mutateAsync({ label: pickTarget.label, standardName: name, standardG2bNo: g2bNo });
            setPickKey(null);
          }}
          onClose={() => setPickKey(null)}
        />
      )}
      {confirmBulk && (
        <StandardBulkConfirm
          rows={sureTodo.map((r) => ({ label: r.it.label, best: r.rec.best! }))}
          saving={bulkMut.isPending}
          error={bulkMut.isError ? (bulkMut.error as Error).message : null}
          onConfirm={() => bulkMut.mutate(sureTodo.map((r) => ({
            label: r.it.label,
            standardName: r.rec.best!.candidate.name,
            standardG2bNo: r.rec.best!.candidate.g2bNo,
          })))}
          onClose={() => { setConfirmBulk(false); bulkMut.reset(); }}
        />
      )}
    </div>
  );
}

/**
 * 「확실한 추천 한 번에 잇기」 확인 창 (v2.316.1 · 독립 검토) — 잇는 것 **전부**를 보여 준다.
 * 확실은 사람이 하나하나 안 보고 이어지므로, 12건만 보이고 「외 N건」으로 숨기면 무엇이 이어지는지 모른 채 누르게 된다.
 */
function StandardBulkConfirm({
  rows,
  saving,
  error,
  onConfirm,
  onClose,
}: {
  rows: { label: string; best: StandardRanked }[];
  saving: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  useDialogLayer(true, null);
  return (
    <ModalShell title={`확실한 추천 ${rows.length}건 한 번에 잇기`} onClose={onClose} wide>
      <p className="text-xs text-slate-500 mb-2">
        아래 SM 내부 명칭에 오른쪽 나라장터 쇼핑몰 등록 명칭을 잇습니다 — 산정근거 최종 문서(엑셀) 품목 칸에 바로 적힙니다
        (이미 만든 산정근거에도 · 나중에 [고르기]·[끊기]로 바꿀 수 있음). 그새 다른 곳에서 이어진 품목은 건드리지 않습니다.
      </p>
      <div className="max-h-[360px] overflow-y-auto border border-slate-200 rounded" data-std-bulk-list={rows.length}>
        <table className="w-full text-xs">
          <tbody>
            {rows.map(({ label, best }) => (
              <tr key={label} className="border-b border-slate-100 last:border-0 align-top">
                <td className="px-3 py-1.5 w-[30%] text-slate-800 break-words">{label}</td>
                <td className="px-2 py-1.5 text-slate-700 break-words" title={`나라장터 등록 명칭: ${best.candidate.name}`}>
                  {standardDocName(best.candidate.name)}
                  <span className="block text-[11px] text-slate-400">{best.why.join(' · ')}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {error && (
        <div className="text-xs bg-rose-50 border border-rose-200 text-rose-700 rounded px-3 py-1.5 mt-2">{error}</div>
      )}
      <div className="flex items-center justify-end gap-2 mt-3">
        <button type="button" onClick={onClose} disabled={saving} className="btn btn-secondary btn-sm">취소</button>
        <button type="button" onClick={onConfirm} disabled={saving} className="btn btn-success btn-sm" data-std-bulk-confirm>
          {saving ? '잇는 중…' : `${rows.length}건 잇기`}
        </button>
      </div>
    </ModalShell>
  );
}

/**
 * 단가표 줄의 「표준 표기」 한 줄 (v2.316.1 · 사용자 「납품항목 → SM 내부 명칭 → 표준 표기(나라장터 쇼핑몰 표기)」).
 * 이어 둔 이름이 있으면 보여 주고 [바꾸기] · 없으면 [＋ 표준 표기 잇기] — 최종 문서 품목 칸에 이 이름이 적힌다.
 */
function StandardNameLine({
  item,
  disabled,
  onPick,
}: {
  item: maintApi.DeliveryPriceItem;
  disabled: boolean;
  onPick: () => void;
}) {
  // 영문·숫자가 없는 품목(서버랙 등)은 이을 수 없다 — 단추를 띄우면 눌러도 서버가 거절한다
  if (!standardLinkable(item)) return null;
  if (item.standardName) {
    return (
      <div className="text-xs text-teal-800 mt-0.5 flex items-start gap-1.5" data-standard-name={item.key}>
        <span className="shrink-0" title="표준 표기 — 산정근거 최종 문서(엑셀) 품목 칸에 이 이름이 적힙니다">📄</span>
        <span className="min-w-0 break-words" title={standardIsSelf(item) ? undefined : `나라장터 등록 명칭: ${item.standardName}`}>
          {standardIsSelf(item) ? '나라장터 등록 명칭 없음 — 문서엔 품목 글 그대로' : standardDocName(item.standardName)}
          {item.standardG2bNo && <span className="text-slate-400"> · {item.standardG2bNo}</span>}
          {standardLabelDiffers(item) && (
            <span className="block text-amber-700">⚠ 「{item.standardLabel}」 이름으로 이어 둔 것 — 이 이름의 품목 글은 안 바뀜</span>
          )}
        </span>
        <button
          type="button"
          disabled={disabled}
          onClick={onPick}
          className="shrink-0 text-slate-400 hover:text-blue-700 underline decoration-dotted underline-offset-2"
          title="다른 나라장터 명칭으로 바꾸거나 연결을 끊습니다"
        >
          바꾸기
        </button>
      </div>
    );
  }
  return (
    <div className="mt-0.5">
      <button
        type="button"
        disabled={disabled}
        onClick={onPick}
        data-standard-pick={item.key}
        className="text-xs text-slate-400 hover:text-teal-700 underline decoration-dotted underline-offset-2"
        title="나라장터 쇼핑몰에 등록된 명칭을 이어 두면 최종 문서(엑셀) 품목 칸에 SM 내부 명칭 대신 적힙니다"
      >
        ＋ 표준 표기(나라장터) 잇기
      </button>
    </div>
  );
}

/**
 * 표준 표기 고르기 창 (v2.316.1 · 사용자 「SM 내부 명칭 → 나라장터쇼핑몰 명칭으로 한 번 더 연결」).
 *
 * 후보 = 나라장터쇼핑몰 메뉴의 MAS 제품 풀네임 · 3자단가 등록 규격(서버 /budget/standard-names) — 추천(점수)이 위로.
 * 점수·추천은 lib/budget-standard.ts 한 곳(매칭 화면 · 한 번에 잇기와 같은 판정).
 * 목록에 없는 명칭은 아래 칸에 직접 적는다(물품식별번호 없이 저장).
 * 입력 창 — 배경·ESC 로 안 닫힘(✕ · [취소] 만) · 아래 창으로 ESC 가 새지 않게 층만 쌓는다.
 */
function StandardNamePicker({
  item,
  onSave,
  onClose,
}: {
  item: maintApi.DeliveryPriceItem;
  /** name = null 이면 연결 끊기 */
  onSave: (name: string | null, g2bNo: string | null) => Promise<void>;
  onClose: () => void;
}) {
  useDialogLayer(true, null);
  const query = useQuery({
    queryKey: ['maintenance', 'standardNames'],
    queryFn: maintApi.listStandardNames,
    staleTime: 60_000,
  });
  const [kw, setKw] = useState('');
  const [picked, setPicked] = useState<{ name: string; g2bNo: string | null } | null>(
    item.standardName ? { name: item.standardName, g2bNo: item.standardG2bNo ?? null } : null,
  );
  /** 목록에 없는 명칭을 직접 적는 칸 — 적으면 고른 목록 줄보다 이것이 이긴다 */
  const [manual, setManual] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const ranked = useMemo(() => {
    const names = query.data?.names ?? [];
    const rec = recommendStandard(item, names);
    return rankStandard(item, names).map((r) => ({
      c: r.candidate,
      why: r.why,
      // 확실은 그 한 후보에만(같은 명칭·다른 가격 줄이 있을 수 있어 이름 말고 그 줄로) · 나머지는 추천·가격 같음·없음
      badge: standardBadge(r, rec.sure && rec.best?.candidate === r.candidate),
    }));
  }, [query.data, item]);
  const k = kw.trim().toLowerCase();
  const shown = k === '' ? ranked : ranked.filter((r) => `${r.c.name} ${r.c.g2bNo ?? ''} ${r.c.group}`.toLowerCase().includes(k));
  const choice = manual.trim() !== '' ? { name: manual.trim().replace(/\s+/g, ' '), g2bNo: null } : picked;
  // 이름이 같아도 물품식별번호가 새로 붙으면 바뀐 것(직접 적은 이름을 목록 줄로 다시 고른 경우)
  // · 이은 이름이 지금 이름과 글자가 다르면 같은 것을 다시 저장해도 바뀐 것(지금 이름으로 다시 잇는다)
  const changed = (choice?.name ?? null) !== (item.standardName ?? null)
    || (choice !== null && (choice.g2bNo ?? null) !== (item.standardG2bNo ?? null))
    || (choice !== null && standardLabelDiffers(item));

  async function save(name: string | null, g2bNo: string | null): Promise<void> {
    setSaving(true);
    setErr(null);
    try {
      await onSave(name, g2bNo);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title={`표준 표기 잇기 — ${item.label}`} onClose={onClose} wide>
      <p className="text-xs text-slate-500 mb-2">
        SM 내부 명칭 <b className="text-slate-700">{item.label}</b>
        {item.unitPrice !== null && <> · 단가 {item.unitPrice.toLocaleString('ko-KR')}원</>}
        {' '}— 고른 나라장터 쇼핑몰 등록 명칭이 산정근거 <b>최종 문서(엑셀) 품목 칸</b>에 이 이름 대신 적힙니다
        (앞의 품명은 빼고 · 수량은 「* N」 그대로 · 모든 고객사·이미 만든 산정근거에도 같이 · 품목 글이 이 이름과 글자 그대로 같은 품목만).
      </p>
      {standardLabelDiffers(item) && (
        <p className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-1.5 mb-2">
          ⚠ 지금 연결은 「{item.standardLabel}」 이름으로 이어 둔 것이라, 글자가 다른 「{item.label}」 품목 글은 안 바뀝니다 —
          다시 저장하면 이 이름으로 이어집니다.
        </p>
      )}
      <input
        value={kw}
        onChange={(e) => setKw(e.target.value)}
        placeholder="검색 — 모델·회사·용량·물품식별번호"
        className="ui-input w-full mb-2"
      />
      <div className="max-h-[340px] overflow-y-auto border border-slate-200 rounded" data-standard-candidates={shown.length}>
        {query.isPending && <div className="text-sm text-slate-400 py-8 text-center"><LoadingLines /></div>}
        {query.isError && !query.data && (
          <div className="text-sm text-rose-600 py-8 text-center">목록을 못 불러왔습니다: {(query.error as Error).message}</div>
        )}
        {query.data && shown.length === 0 && (
          <div className="text-sm text-slate-400 py-8 text-center">맞는 나라장터 명칭이 없습니다 — 아래에 직접 적으세요.</div>
        )}
        {shown.map(({ c, why, badge }) => {
          // 저장되는 것은 명칭·물품식별번호 — 그 둘이 같은 줄은 같은 것으로 본다
          const on = manual.trim() === '' && picked?.name === c.name && (picked.g2bNo ?? null) === (c.g2bNo ?? null);
          return (
            <button
              key={`${c.name}|${c.g2bNo ?? ''}|${c.price ?? ''}`}
              type="button"
              onClick={() => { setPicked({ name: c.name, g2bNo: c.g2bNo }); setManual(''); }}
              className={`w-full text-left px-3 py-1.5 border-b border-slate-100 last:border-0 text-xs flex items-start gap-2 ${
                on ? 'bg-teal-50' : 'hover:bg-slate-50'
              }`}
            >
              <span className={`shrink-0 mt-0.5 w-3 h-3 rounded-full border ${on ? 'bg-teal-600 border-teal-600' : 'border-slate-300'}`} />
              <span className="min-w-0 flex-1">
                {/* 문서에 적히는 꼴(앞의 품명 뺌)을 크게 · 품명은 고를 때 보탬이 되게 아래 작은 글씨로 */}
                <span className="text-slate-800 break-words" data-standard-cand-name>{standardDocName(c.name)}</span>
                <span className="block text-[11px] text-slate-400">
                  {(() => {
                    const cat = standardCategoryOf(c.name);
                    return cat !== null && !c.group.includes(cat) ? <>{cat} · </> : null;
                  })()}
                  {c.source} · {c.group}
                  {c.g2bNo && <> · {c.g2bNo}</>}
                  {c.price !== null && <> · {c.price.toLocaleString('ko-KR')}원</>}
                </span>
              </span>
              {badge !== null && (
                <span
                  className={`shrink-0 px-1.5 py-0.5 rounded border text-[11px] ${
                    badge === 'sure'
                      ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                      : badge === 'recommend'
                        ? 'bg-amber-50 border-amber-300 text-amber-700'
                        : 'bg-slate-50 border-slate-300 text-slate-600'
                  }`}
                  title={why.join(' · ')}
                >
                  {badge === 'sure' ? '확실' : badge === 'recommend' ? '추천' : '가격만 같음'} · {why.join(' · ')}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <label className="block text-xs text-slate-500 mt-3">
        목록에 없으면 직접 적기 (나라장터 쇼핑몰에 등록된 그대로 · 한 줄)
        <input
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          placeholder="예) 하드디스크드라이브, Infortrend technology, (TW)HELH72S3T16, 16TB"
          className="ui-input w-full mt-1"
        />
      </label>
      {manual.trim() !== '' && (
        <div className="text-[11px] text-teal-800 mt-1" data-standard-manual-doc>
          📄 문서엔: {standardDocName(manual.replace(/\s+/g, ' '))}
        </div>
      )}
      {/*
        나라장터에 등록 명칭이 없는 품목(PowerEdge R760 등) — 그대로 두면 문서마다 「⚠ 표준 표기 없음」이 남는다.
        SM 내부 명칭 자신을 표준 표기로 정해 「이 이름이 맞다」고 적어 두면 ⚠ 가 사라진다.
      */}
      <button
        type="button"
        disabled={saving || standardIsSelf(item)}
        onClick={() => {
          if (window.confirm(`'${item.label}' — 나라장터 쇼핑몰에 등록 명칭이 없다고 정할까요?\n최종 문서엔 산정근거 품목 글이 그대로 적히고 「⚠ 표준 표기 없음」이 사라집니다.`)) {
            void save(item.label, null);
          }
        }}
        className="btn btn-secondary btn-sm mt-2"
        title="나라장터에 없는 품목 — 최종 문서에 SM 내부 명칭 그대로 적고 ⚠ 를 없앱니다"
        data-standard-self
      >
        {standardIsSelf(item) ? '지금: 나라장터 등록 명칭 없음(품목 글 그대로)' : '나라장터 등록 명칭 없음 — 문서엔 품목 글 그대로'}
      </button>
      {err && (
        <div className="text-xs bg-rose-50 border border-rose-200 text-rose-700 rounded px-3 py-1.5 mt-2">{err}</div>
      )}
      <div className="flex items-center gap-2 mt-3">
        {item.standardName && (
          <button
            type="button"
            disabled={saving}
            onClick={() => {
              if (window.confirm(`'${item.label}' 의 표준 표기 연결을 끊을까요?\n최종 문서에 SM 내부 명칭 그대로 적힙니다.`)) void save(null, null);
            }}
            className="btn btn-outline btn-sm"
          >
            연결 끊기
          </button>
        )}
        <span className="text-xs text-slate-500 ml-auto truncate max-w-[50%]" title={choice?.name}>
          {choice ? <>고른 것: {standardDocName(choice.name)}</> : '아직 고르지 않았습니다'}
        </span>
        <button type="button" onClick={onClose} className="btn btn-secondary btn-sm">취소</button>
        <button
          type="button"
          disabled={saving || choice === null || !changed}
          onClick={() => { if (choice) void save(choice.name, choice.g2bNo); }}
          className="btn btn-primary btn-sm"
          data-standard-save
        >
          {saving ? '저장 중…' : '저장'}
        </button>
      </div>
    </ModalShell>
  );
}

/**
 * 산정근거 행 아래 「📄 문서 표기」 (v2.316.1 · 이은 품목만 v2.316.3) — 최종 문서(엑셀) 품목 칸에 실제로 적히는 것.
 * 저장된 글 기준이라 품목 칸을 고치면 저장한 뒤에 다시 보인다(서버가 엑셀과 같은 함수로 만든다).
 * 바뀐 토막이 없으면(docConverted=false) 문서엔 품목 글 그대로라 📄 줄은 빼고 ⚠ 만.
 * 이은 품목만 바뀌므로 한 칸 안에 표준 표기와 SM 내부 명칭이 섞일 수 있다 — 안 바뀐 줄은 흐리게.
 */
function DocNameHint({ docLines, docConverted, noStandard, unconvertible }: {
  docLines?: { text: string; std: boolean }[]; docConverted?: boolean; noStandard?: string[]; unconvertible?: string[];
}) {
  if (docLines === undefined || docLines.length === 0) return null;
  const missing = noStandard ?? [];
  const kept = unconvertible ?? [];
  return (
    <>
      {docConverted === true && (
        <div
          className="mt-0.5 flex items-start gap-1 text-[11px] leading-4"
          title="최종 문서(엑셀) 품목 칸에 이렇게 적힙니다 — 「표준 표기 매칭」에서 이어 둔 표준 표기(나라장터 명칭 · 앞의 품명은 뺌) · 흐린 줄은 표준 표기가 없어 SM 내부 명칭 그대로"
          data-doc-item
        >
          <span className="shrink-0">📄</span>
          <span className="min-w-0">
            {docLines.map((l, i) => (
              <span
                key={i}
                className={`block break-words ${l.std ? 'text-teal-800' : 'text-slate-500'}`}
                data-doc-line={l.std ? 'std' : 'raw'}
              >
                {l.text}
              </span>
            ))}
          </span>
        </div>
      )}
      {missing.length > 0 && (
        <div
          className="text-xs text-amber-700 mt-0.5"
          title="「표준 표기 매칭」(또는 단가표 작성)에서 이 품목에 표준 표기를 이으면 문서에 그 이름이 적힙니다 — 이은 품목만 바뀌고 이 품목은 SM 내부 명칭 그대로 적힙니다"
          data-no-standard
        >
          ⚠ 표준 표기 없음(SM 내부 명칭 그대로): {missing.join(', ')}
        </div>
      )}
      {kept.length > 0 && (
        <div
          className="text-xs text-amber-700 mt-0.5"
          title="문서는 품목 글이 이어 둔 SM 내부 명칭과 글자 그대로 같을 때만 바꿉니다 — 틀린 제품·수량이 적히는 것보다 글 그대로가 낫습니다. 품목 칸을 「SM 내부 명칭 * 수량」 꼴로 고치면 바뀝니다."
          data-unconvertible
        >
          {kept.map((k) => (
            <div key={k}>⚠ 표준 표기로 못 바꿈(글 그대로): {k}</div>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * 단가를 모르는 품목이 있으면 알려 주는 보조줄.
 *
 * 금액이 '일부만' 더해진 행을 사람이 모르고 지나치지 않게 하려는 것 —
 * 모르는 품목을 조용히 0 원으로 세면 합계가 틀린 줄도 모르게 된다.
 */
function UnpricedHint({ items }: { items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="text-xs text-amber-700 mt-0.5" title="「단가표 작성」에 이 SM 내부 명칭의 단가가 없어 금액에 안 들어갔습니다 — 거기서 단가를 넣으면 채워집니다">
      ⚠ 단가 없음: {items.join(', ')}
    </div>
  );
}

/**
 * 장비와 디스크가 한 행에 따로 적혀 금액을 두 번 셀 수 있는 경우의 보조줄.
 *
 * 예) 'US216D * 1, SM 16TB * 7' — US216D 등록가 안에 이미 디스크가 들어 있는데
 *     'SM 16TB' 단가를 또 더하면 실제보다 커진다.
 * ⭐ 사용자 결정(2026-08-10): 자동으로 고치지 않고 **경고만** — 장비 단가를 본체값으로
 *    낮출지, 디스크 줄을 지울지는 사람이 판단한다.
 */
/**
 * 분류가 정해지지 않아 **이 분류에 넣지 않은** 항목 (v2.269.1 · 사용자 「스토리지를 골랐는데 vSphere 가 섞여 보인다」).
 * 전에는 건이 걸린 분류마다 다 넣었다 — 이제는 섞지 않고 여기 알린다. 「단가표 작성」에서 품목의
 * 분류(서버·스토리지·소프트웨어·넣지 않음)를 고르면 그다음 불러오기부터 그 분류로 들어간다.
 */
function UnclassifiedHint({ items }: { items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div
      className="text-xs text-violet-700 mt-0.5"
      title="이 항목은 분류를 몰라 이 산정근거에 넣지 않았습니다 — 「단가표 작성」에서 분류를 고르세요"
      data-unclassified
    >
      ⚠ 분류 미정 (이 분류에 안 넣음): {items.join(', ')}
    </div>
  );
}

function DoubleCountHint({ items }: { items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div
      className="text-xs text-rose-600 mt-0.5"
      title="장비 도입금액 안에 디스크가 이미 들어 있으면 금액이 두 번 셉니다. 장비 단가를 본체값으로 낮추거나 디스크를 빼세요."
    >
      ⚠ 장비+디스크 한 행: {items.join(', ')} — 금액 이중계산 확인
    </div>
  );
}

/* ── 최종 산출 ─────────────────────────────────────── */

/**
 * 고객사 하나의 세 분류를 한 장에 — 도입금액 · 요율 · 유지보수료.
 *
 * 분류 버튼을 하나씩 눌러 봐야 총액을 알 수 있으면 '최종 산출' 이 아니다.
 * 그래서 산정근거 화면 맨 위에 늘 띄워 두고, 줄을 누르면 그 분류로 바로 간다.
 */
function FinalSummary({
  data,
  loading,
  current,
  onPick,
}: {
  data?: maintApi.BudgetSummary;
  loading: boolean;
  current: BudgetCategory | null;
  onPick: (c: BudgetCategory) => void;
}) {
  if (loading || !data) {
    return (
      <div className="bg-white rounded border border-slate-200 px-4 py-3 text-xs text-slate-500">
        최종 산출 불러오는 중…
      </div>
    );
  }

  const anyWarn = data.categories.some((c) => c.unpricedRows > 0 || c.doubleCountRows > 0 || (c.unclassifiedRows ?? 0) > 0);

  return (
    <div className="bg-white rounded border border-teal-200 overflow-hidden">
      <div className="px-3 py-1.5 bg-teal-50 border-b border-teal-200 text-xs font-medium text-teal-800">
        최종 산출 — 유지보수료 = 도입금액 합계 × 요율(%)
      </div>
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-slate-500 text-xs border-b border-slate-100">
            <th className="text-left font-medium px-3 py-1.5 w-[110px]">분류</th>
            <th className="text-right font-medium px-2 py-1.5 w-[70px]">행수</th>
            <th className="text-right font-medium px-2 py-1.5 w-[150px]">도입금액</th>
            <th className="text-right font-medium px-2 py-1.5 w-[80px]">요율</th>
            <th className="text-right font-medium px-2 py-1.5 w-[150px]">연간 유지보수료</th>
            <th className="text-left font-medium px-3 py-1.5">확인 필요</th>
          </tr>
        </thead>
        <tbody>
          {data.categories.map((c) => (
            <tr
              key={c.category}
              onClick={() => onPick(c.category)}
              className={`border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 ${
                current === c.category ? 'bg-indigo-50/60' : ''
              }`}
            >
              <td className="px-3 py-1.5">
                <span className={`text-xs px-1.5 py-0.5 rounded ${CAT_BADGE[c.category]}`}>
                  {BUDGET_CATEGORY_LABEL[c.category]}
                </span>
              </td>
              {!c.exists ? (
                <td colSpan={5} className="px-2 py-1.5 text-xs text-slate-400">
                  작성 안 함
                </td>
              ) : (
                <>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">{c.rows}행</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{won(c.amount)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">
                    {c.ratePercent === null ? (
                      <span className="text-amber-600 text-xs">미정</span>
                    ) : (
                      `${c.ratePercent}%`
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-medium text-teal-700">
                    {c.fee === null ? <span className="text-slate-300">-</span> : won(c.fee)}
                  </td>
                  <td className="px-3 py-1.5 text-xs">
                    {c.unpricedRows > 0 && (
                      <span className="text-amber-700 mr-2">⚠ 단가 없음 {c.unpricedRows}행</span>
                    )}
                    {c.doubleCountRows > 0 && (
                      <span className="text-rose-600 mr-2">⚠ 장비+디스크 {c.doubleCountRows}행</span>
                    )}
                    {(c.unclassifiedRows ?? 0) > 0 && (
                      <span
                        className="text-violet-700"
                        title="분류가 정해지지 않아 이 분류에 넣지 않은 항목이 있는 행 — 「단가표 작성」에서 품목의 분류를 고르세요"
                      >
                        ⚠ 분류 미정 {c.unclassifiedRows}행
                      </span>
                    )}
                    {(c.noStandardRows ?? 0) > 0 && (
                      <span
                        className="text-amber-700 ml-2"
                        title="표준 표기(나라장터 쇼핑몰 등록 명칭)를 안 이은 품목이 있는 행 — 최종 문서에 SM 내부 명칭 그대로 적힙니다 · 「단가표 작성」에서 이으세요"
                      >
                        ⚠ 표준 표기 없음 {c.noStandardRows}행
                      </span>
                    )}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-slate-50 border-t border-slate-200 font-bold text-slate-800">
            <td className="px-3 py-2 text-xs">합계</td>
            <td />
            <td className="px-2 py-2 text-right tabular-nums">{won(data.totalAmount)}</td>
            <td />
            <td className="px-2 py-2 text-right tabular-nums text-teal-700">
              {data.totalFee === null ? (
                <span className="text-amber-600 text-xs font-normal">요율 미정</span>
              ) : (
                won(data.totalFee)
              )}
            </td>
            <td className="px-3 py-2 text-xs font-normal text-slate-500">
              {anyWarn
                ? '⚠ 표시된 행은 금액이 흔들릴 수 있습니다 — 확인하세요'
                : data.totalFee === null
                ? '요율을 넣으면 유지보수료가 나옵니다'
                : ''}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/** 숫자 칸 — 다 치고 칸을 벗어날 때(또는 Enter) 저장한다 */
function NumberCell({
  value,
  placeholder,
  allowDecimal,
  onCommit,
}: {
  value: number | null;
  placeholder?: string;
  allowDecimal?: boolean;
  onCommit: (v: number | null) => void;
}) {
  const [text, setText] = useState<string | null>(null);
  const shown = text ?? (value === null ? '' : String(value));

  function commit() {
    if (text === null) return;
    const clean = text.replace(allowDecimal ? /[^0-9.]/g : /[^0-9]/g, '');
    const next = clean === '' ? null : Number(clean);
    setText(null);
    if (next !== value && (next === null || Number.isFinite(next))) onCommit(next);
  }

  return (
    <input
      value={shown}
      inputMode={allowDecimal ? 'decimal' : 'numeric'}
      placeholder={placeholder}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') setText(null);
      }}
      className="ui-input ui-input-sm w-full text-right tabular-nums"
    />
  );
}

/* ═══ 공통 조각 ════════════════════════════════════════ */

function ViewHeader({
  onBack,
  title,
  subtitle,
  right,
}: {
  onBack: () => void;
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="btn btn-secondary"
        >
          ← 고객사 목록
        </button>
        <div>
          <h2 className="ui-section-title">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`btn btn-sm ${active ? 'btn-dark' : 'btn-secondary'}`}
    >
      {label}
    </button>
  );
}

function ModalShell({
  title,
  onClose,
  children,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /** 표가 들어가는 팝업용 — 매입내역처럼 칼럼이 많으면 넓게 */
  wide?: boolean;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
    >
      <div className={`bg-white rounded shadow-xl w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} p-5`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="ui-subsection-title">{title}</h3>
          <button type="button" onClick={onClose} className="ui-close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
